/**
 * Elite Batch 18 Part 1 — Workflow Templates 1648–1667
 * Proposal viewed notification, proposal viewed no response, quote approved internal handoff,
 * quote sent confirmation, RFP procurement inquiry follow-up, ROI calculator completion follow-up,
 * ROI calculator follow-up to meeting, real estate lead nurture drip, reference request workflow,
 * renewal opportunity creation, renewal cross-sell nurture, repeat site visit nurture,
 * request-a-quote nurture, research report release nurture, SDR handoff to AE automation,
 * SDR no follow-up escalation, SMS lead qualification flow, sales call sentiment analyzer,
 * sales forecast generator, sales leaderboard digest.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function hubspotGetNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "GET",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables: vars,
      },
    },
    x,
  );
}
function hubspotPostNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  bodyFields: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyFields,
        variables: vars,
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 18 PART 1 — TEMPLATES 1648–1667
// =============================================================================

export const ELITE_BATCH18_PART1: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 1648. PROPOSAL VIEWED NOTIFICATION (8 nodes)
  // ===================================================================

  "Proposal Viewed Notification": {
    summary:
      "Receives a PandaDoc proposal view event via webhook, extracts view metadata with runCode, looks up the associated CRM deal in HubSpot, uses AI to calculate an engagement score based on view duration and page depth, branches on high engagement, alerts the sales rep via Slack, sends an email notification to the rep, and logs the activity back to the CRM.",
    nodes: [
      trigger("Webhook: PandaDoc proposal viewed", "webhook_app", {
        source: "pandadoc",
        event: "document.viewed",
        howItStarts:
          "Fires when a recipient views a PandaDoc proposal document, sending the raw view event payload including document ID, viewer details, view duration, and page-by-page analytics",
        setup:
          "Configure PandaDoc webhook for document.viewed events. Ensure HubSpot API token has deal read/write access. Set up Slack webhook for sales channel. Map PandaDoc document IDs to HubSpot deal IDs via custom properties.",
        inputFields: [],
      }),

      // 2. View metadata extractor runCode
      n("2", "runCode", "Extract and normalize PandaDoc view metadata", 280, {
        runCodeDetails: {
          code: `const payload = JSON.parse(variables.webhook_payload || '{}');
const data = payload.data || payload;
const doc = data.document || data;
const viewer = data.recipient || data.viewer || {};
output.document_id = doc.id || doc.document_id || '';
output.document_name = doc.name || doc.title || '';
output.viewer_email = viewer.email || viewer.recipient_email || '';
output.viewer_name = (viewer.first_name || '') + ' ' + (viewer.last_name || '');
output.view_duration_seconds = parseInt(data.duration || data.view_duration || data.time_spent || '0');
output.pages_viewed = parseInt(data.pages_viewed || data.page_count || '0');
output.total_pages = parseInt(doc.total_pages || doc.pages || '0');
output.completion_pct = output.total_pages > 0 ? Math.round((output.pages_viewed / output.total_pages) * 100) : 0;
output.view_timestamp = data.created_at || data.timestamp || new Date().toISOString();
output.deal_id = doc.metadata?.hubspot_deal_id || doc.external_id || '';`,
          outputVariables: [
            { name: "document_id", type: "string" },
            { name: "document_name", type: "string" },
            { name: "viewer_email", type: "string" },
            { name: "viewer_name", type: "string" },
            { name: "view_duration_seconds", type: "number" },
            { name: "pages_viewed", type: "number" },
            { name: "total_pages", type: "number" },
            { name: "completion_pct", type: "number" },
            { name: "view_timestamp", type: "string" },
            { name: "deal_id", type: "string" },
          ],
        },
      }),

      // 3. CRM deal lookup HubSpot
      hubspotGetNode(
        "3",
        "Fetch CRM deal and owner details from HubSpot",
        520,
        "crm/v3/objects/deals/{deal_id}?properties=dealname,amount,dealstage,hubspot_owner_id,pipeline,closedate&associations=contacts",
        [
          {
            name: "properties.dealname",
            customName: "deal_name",
            type: "string",
          },
          {
            name: "properties.amount",
            customName: "deal_amount",
            type: "string",
          },
          {
            name: "properties.dealstage",
            customName: "deal_stage",
            type: "string",
          },
          {
            name: "properties.hubspot_owner_id",
            customName: "owner_id",
            type: "string",
          },
          {
            name: "properties.pipeline",
            customName: "pipeline_name",
            type: "string",
          },
          {
            name: "properties.closedate",
            customName: "expected_close",
            type: "string",
          },
        ],
      ),

      // 4. AI engagement score calculator
      aiNode(
        "4",
        "AI calculate proposal engagement score",
        760,
        "You are a sales engagement analyst. Given proposal view metrics and deal context, calculate a 0-100 engagement score and determine if this is a high-engagement signal. Consider: view duration (>120s is strong), completion percentage (>75% is strong), deal stage progression, and deal value. Return JSON: {engagement_score, engagement_level, key_signal, recommended_action, rep_talking_point, urgency}",
        "Document: {document_name} | Duration: {view_duration_seconds}s | Pages: {pages_viewed}/{total_pages} ({completion_pct}%) | Deal: {deal_name} | Amount: ${deal_amount} | Stage: {deal_stage} | Pipeline: {pipeline_name}",
        [
          { field: "engagement_score", as: "engagement_score", type: "number" },
          { field: "engagement_level", as: "engagement_level", type: "string" },
          { field: "key_signal", as: "key_signal", type: "string" },
          {
            field: "recommended_action",
            as: "recommended_action",
            type: "string",
          },
          {
            field: "rep_talking_point",
            as: "rep_talking_point",
            type: "string",
          },
          { field: "urgency", as: "view_urgency", type: "string" },
        ],
      ),

      // 5. High-engagement branch condition
      n("5", "condition", "Check if engagement level is high", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "engagement_level", operator: "equals", value: "high" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 6. Sales rep Slack alert (True path)
      slackNode(
        "6",
        "Alert sales rep via Slack for high engagement",
        1240,
        "🔥 *High-Engagement Proposal View* 🔥\\n*Prospect:* {viewer_name} ({viewer_email})\\n*Document:* {document_name}\\n*Duration:* {view_duration_seconds}s | *Pages:* {pages_viewed}/{total_pages} ({completion_pct}%)\\n*Engagement Score:* {engagement_score}/100\\n*Signal:* {key_signal}\\n*Deal:* {deal_name} (${deal_amount}) — {deal_stage}\\n*Action:* {recommended_action}\\n*Talking Point:* {rep_talking_point}",
      ),

      // 7. Email notification to rep (True path, after Slack)
      emailNode(
        "7",
        "Email detailed view report to sales rep",
        1480,
        "{owner_id}@company.com",
        "Proposal Viewed: {document_name} — Engagement Score {engagement_score}/100",
        "Hi,\\n\\nYour proposal was just viewed by {viewer_name} ({viewer_email}).\\n\\nView Details:\\n- Duration: {view_duration_seconds} seconds\\n- Pages viewed: {pages_viewed} of {total_pages} ({completion_pct}% completion)\\n- Engagement Score: {engagement_score}/100 ({engagement_level})\\n\\nDeal Context:\\n- Deal: {deal_name}\\n- Amount: ${deal_amount}\\n- Stage: {deal_stage}\\n- Expected Close: {expected_close}\\n\\nKey Signal: {key_signal}\\nRecommended Action: {recommended_action}\\nTalking Point: {rep_talking_point}\\n\\nUrgency: {view_urgency}\\n\\nAct quickly — high-engagement views often precede buying decisions.",
      ),

      // 8. CRM activity log HubSpot
      hubspotPostNode(
        "8",
        "Log proposal view activity to CRM deal",
        1720,
        "crm/v3/objects/deals/{deal_id}",
        [
          {
            key: "properties",
            value:
              '{"notes_last_updated":"Proposal viewed by {viewer_name} ({viewer_email}). Duration: {view_duration_seconds}s. Pages: {pages_viewed}/{total_pages}. Engagement: {engagement_score}/100 ({engagement_level}). Signal: {key_signal}."}',
          },
        ],
        [
          { name: "id", customName: "updated_deal_id", type: "string" },
          {
            name: "properties.notes_last_updated",
            customName: "activity_logged",
            type: "string",
          },
        ],
        400,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "8", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1649. PROPOSAL VIEWED NO RESPONSE (10 nodes)
  // ===================================================================

  "Proposal Viewed No Response Follow-Up": {
    summary:
      "Receives a PandaDoc view event, waits 48 hours for a response window, checks PandaDoc document status via API, branches on no response, fetches CRM contact and deal context from HubSpot, uses AI to draft a follow-up email, sends the follow-up, waits another 72 hours for engagement, escalates to manager via Slack, and updates the CRM deal stage in HubSpot.",
    nodes: [
      trigger("Webhook: PandaDoc proposal viewed", "webhook_app", {
        source: "pandadoc",
        event: "document.viewed",
        howItStarts:
          "Fires when a recipient views a PandaDoc proposal, initiating a timed follow-up sequence if no response is received within the response window",
        setup:
          "Configure PandaDoc webhook for document.viewed events. Set PandaDoc API key for status checks. Ensure HubSpot API token has contact and deal read/write access. Configure Slack webhook for manager escalation channel.",
        inputFields: [],
      }),

      // 2. Response window timer delay 48hrs
      n("2", "delayAction", "Wait 48-hour response window", 280, {
        delayDetails: { duration: 48, unit: "hours" },
      }),

      // 3. PandaDoc status check API
      n("3", "hitApi", "Check PandaDoc document current status", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.pandadoc.com/public/v1/documents/{document_id}",
          headers: [
            { key: "Authorization", value: "API-Key {pandadoc_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "status", customName: "doc_status", type: "string" },
            { name: "name", customName: "doc_name", type: "string" },
            {
              name: "date_created",
              customName: "doc_created_date",
              type: "string",
            },
            {
              name: "date_modified",
              customName: "doc_modified_date",
              type: "string",
            },
            {
              name: "recipients[0].email",
              customName: "recipient_email",
              type: "string",
            },
            {
              name: "recipients[0].first_name",
              customName: "recipient_first_name",
              type: "string",
            },
            {
              name: "recipients[0].last_name",
              customName: "recipient_last_name",
              type: "string",
            },
            {
              name: "metadata.hubspot_deal_id",
              customName: "deal_id",
              type: "string",
            },
            {
              name: "grand_total.amount",
              customName: "proposal_amount",
              type: "string",
            },
          ],
        },
      }),

      // 4. No response condition
      n("4", "condition", "Check if proposal still has no response", 760, {
        conditionDetails: {
          conditions: [
            {
              variable: "doc_status",
              operator: "equals",
              value: "document.viewed",
            },
          ],
          logicalOperator: "AND",
        },
      }),

      // 5. CRM contact deal fetch HubSpot (True path — no response)
      hubspotGetNode(
        "5",
        "Fetch contact and deal context from HubSpot",
        1000,
        "crm/v3/objects/deals/{deal_id}?properties=dealname,amount,dealstage,hubspot_owner_id,closedate,hs_deal_stage_probability,notes_last_contacted&associations=contacts",
        [
          {
            name: "properties.dealname",
            customName: "deal_name",
            type: "string",
          },
          {
            name: "properties.amount",
            customName: "deal_amount",
            type: "string",
          },
          {
            name: "properties.dealstage",
            customName: "deal_stage",
            type: "string",
          },
          {
            name: "properties.hubspot_owner_id",
            customName: "deal_owner_id",
            type: "string",
          },
          {
            name: "properties.closedate",
            customName: "deal_close_date",
            type: "string",
          },
          {
            name: "properties.hs_deal_stage_probability",
            customName: "win_probability",
            type: "string",
          },
          {
            name: "properties.notes_last_contacted",
            customName: "last_contact_date",
            type: "string",
          },
        ],
      ),

      // 6. AI follow-up email drafter
      aiNode(
        "6",
        "AI draft personalized follow-up email",
        1240,
        "You are a sales follow-up copywriter. The prospect viewed a proposal 48 hours ago but has not responded. Craft a warm, non-pushy follow-up email that references the specific proposal, addresses potential concerns, and offers to answer questions. Do not be aggressive. Return JSON: {subject_line, email_body, tone_used, objection_addressed}",
        "Proposal: {doc_name} | Amount: ${proposal_amount} | Recipient: {recipient_first_name} {recipient_last_name} ({recipient_email}) | Deal: {deal_name} | Stage: {deal_stage} | Win Probability: {win_probability}% | Last Contact: {last_contact_date}",
        [
          { field: "subject_line", as: "followup_subject", type: "string" },
          { field: "email_body", as: "followup_body", type: "string" },
          { field: "tone_used", as: "followup_tone", type: "string" },
          {
            field: "objection_addressed",
            as: "objection_addressed",
            type: "string",
          },
        ],
      ),

      // 7. Follow-up email send
      emailNode(
        "7",
        "Send follow-up email to prospect",
        1480,
        "{recipient_email}",
        "{followup_subject}",
        "{followup_body}",
      ),

      // 8. Engagement monitor delay 72hrs
      n("8", "delayAction", "Wait 72 hours for engagement response", 1720, {
        delayDetails: { duration: 72, unit: "hours" },
      }),

      // 9. Escalation to manager Slack
      slackNode(
        "9",
        "Escalate unresponsive proposal to sales manager",
        1960,
        "⚠️ *Proposal No-Response Escalation*\\n*Prospect:* {recipient_first_name} {recipient_last_name} ({recipient_email})\\n*Proposal:* {doc_name} | Amount: ${proposal_amount}\\n*Deal:* {deal_name} — {deal_stage}\\n*Status:* Viewed 5+ days ago, no response after follow-up\\n*Win Probability:* {win_probability}%\\n*Close Date:* {deal_close_date}\\n*Objection Addressed:* {objection_addressed}\\n*Action Required:* Manager intervention recommended",
      ),

      // 10. CRM deal stage update HubSpot
      hubspotPostNode(
        "10",
        "Update CRM deal with stalled proposal flag",
        2200,
        "crm/v3/objects/deals/{deal_id}",
        [
          {
            key: "properties",
            value:
              '{"notes_last_updated":"Proposal {doc_name} viewed but no response after 5 days. Follow-up sent. Escalated to manager. Objection addressed: {objection_addressed}.","hs_deal_stage_probability":"25"}',
          },
        ],
        [
          { name: "id", customName: "updated_deal_id", type: "string" },
          {
            name: "properties.notes_last_updated",
            customName: "stall_note_logged",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
      e("9", "10"),
    ],
  },

  // ===================================================================
  // 1650. QUOTE APPROVED INTERNAL HANDOFF (9 nodes)
  // ===================================================================

  "Quote Approved Internal Handoff": {
    summary:
      "Receives a CPQ quote-approved webhook, extracts quote data with runCode, checks compliance requirements via condition, notifies legal via Slack, alerts finance via Slack, kicks off onboarding in Asana, updates the CRM opportunity in HubSpot, sends a customer welcome email, and handles errors with runCode.",
    nodes: [
      trigger("Webhook: CPQ quote approved", "webhook_app", {
        source: "salesforce_cpq",
        event: "quote.approved",
        howItStarts:
          "Fires when a CPQ quote receives final approval, signaling the deal is ready for internal handoff to legal, finance, and onboarding teams",
        setup:
          "Configure CPQ webhook for quote.approved events. Set up Slack webhooks for legal, finance, and onboarding channels. Provide Asana API token for project creation. Ensure HubSpot API token has deal write access.",
        inputFields: [],
      }),

      // 2. Quote data extraction runCode
      n("2", "runCode", "Extract and validate quote approval data", 280, {
        runCodeDetails: {
          code: `const payload = JSON.parse(variables.webhook_payload || '{}');
const quote = payload.data || payload.quote || payload;
output.quote_id = quote.id || quote.quote_id || '';
output.quote_number = quote.number || quote.quote_number || '';
output.quote_amount = parseFloat(quote.amount || quote.total || quote.grand_total || '0').toFixed(2);
output.currency = quote.currency || 'USD';
output.customer_name = quote.account?.name || quote.customer_name || '';
output.customer_email = quote.contact?.email || quote.customer_email || '';
output.deal_id = quote.opportunity_id || quote.deal_id || '';
output.approved_by = quote.approved_by?.name || quote.approver || '';
output.approval_date = quote.approved_date || new Date().toISOString();
output.payment_terms = quote.payment_terms || 'Net 30';
output.contract_length = quote.subscription_term || quote.contract_months || '12';
output.requires_legal = parseFloat(output.quote_amount) > 50000 ? 'yes' : 'no';
output.line_items_summary = (quote.line_items || []).map(li => li.name + ': $' + li.total).join(', ') || 'See quote for details';`,
          outputVariables: [
            { name: "quote_id", type: "string" },
            { name: "quote_number", type: "string" },
            { name: "quote_amount", type: "string" },
            { name: "currency", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "customer_email", type: "string" },
            { name: "deal_id", type: "string" },
            { name: "approved_by", type: "string" },
            { name: "approval_date", type: "string" },
            { name: "payment_terms", type: "string" },
            { name: "contract_length", type: "string" },
            { name: "requires_legal", type: "string" },
            { name: "line_items_summary", type: "string" },
          ],
        },
      }),

      // 3. Compliance check condition
      n("3", "condition", "Check if quote requires legal review", 520, {
        conditionDetails: {
          conditions: [
            { variable: "requires_legal", operator: "equals", value: "yes" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 4. Legal team notification Slack (True path)
      slackNode(
        "4",
        "Notify legal team for contract review",
        760,
        "📋 *Legal Review Required — Quote Approved*\\n*Quote:* #{quote_number} | Amount: ${quote_amount} {currency}\\n*Customer:* {customer_name}\\n*Approved By:* {approved_by}\\n*Contract Length:* {contract_length} months\\n*Payment Terms:* {payment_terms}\\n*Items:* {line_items_summary}\\n*Action:* Please prepare and review the contract for deals over $50K",
      ),

      // 5. Finance team alert Slack
      slackNode(
        "5",
        "Alert finance team for billing setup",
        1000,
        "💰 *New Quote Approved — Billing Setup Required*\\n*Quote:* #{quote_number} | Amount: ${quote_amount} {currency}\\n*Customer:* {customer_name} ({customer_email})\\n*Payment Terms:* {payment_terms}\\n*Contract Length:* {contract_length} months\\n*Approved By:* {approved_by} on {approval_date}\\n*Action:* Set up invoicing and payment collection",
      ),

      // 6. Onboarding team kickoff Asana API
      n("6", "hitApi", "Create onboarding project in Asana", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://app.asana.com/api/1.0/projects",
          headers: [
            { key: "Authorization", value: "Bearer {asana_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "data",
              value:
                '{"name":"Onboarding: {customer_name} — Quote #{quote_number}","workspace":"{asana_workspace_id}","team":"{asana_team_id}","notes":"Customer: {customer_name}\\nQuote Amount: ${quote_amount}\\nContract: {contract_length} months\\nPayment Terms: {payment_terms}\\nApproved: {approval_date}","due_on":"' +
                new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
                  .toISOString()
                  .split("T")[0] +
                '"}',
            },
          ],
          variables: [
            {
              name: "data.gid",
              customName: "asana_project_id",
              type: "string",
            },
            {
              name: "data.name",
              customName: "asana_project_name",
              type: "string",
            },
            {
              name: "data.permalink_url",
              customName: "asana_project_url",
              type: "string",
            },
          ],
        },
      }),

      // 7. CRM opportunity update HubSpot
      hubspotPostNode(
        "7",
        "Update CRM opportunity to closed-won",
        1480,
        "crm/v3/objects/deals/{deal_id}",
        [
          {
            key: "properties",
            value:
              '{"dealstage":"closedwon","amount":"{quote_amount}","notes_last_updated":"Quote #{quote_number} approved by {approved_by}. Onboarding project created: {asana_project_url}. Legal review: {requires_legal}. Payment terms: {payment_terms}."}',
          },
        ],
        [
          { name: "id", customName: "updated_deal_id", type: "string" },
          {
            name: "properties.dealstage",
            customName: "new_deal_stage",
            type: "string",
          },
        ],
      ),

      // 8. Customer welcome email
      emailNode(
        "8",
        "Send customer welcome and next steps email",
        1720,
        "{customer_email}",
        "Welcome aboard, {customer_name} — Your quote #{quote_number} is confirmed!",
        "Hi {customer_name},\\n\\nGreat news! Your quote #{quote_number} has been officially approved and we are thrilled to have you on board.\\n\\nQuote Summary:\\n- Amount: ${quote_amount} {currency}\\n- Contract Length: {contract_length} months\\n- Payment Terms: {payment_terms}\\n\\nWhat happens next:\\n1. Our onboarding team will reach out within 24 hours to schedule your kickoff call\\n2. You will receive contract documents for review and signature\\n3. We will begin setting up your account and access\\n\\nIf you have any immediate questions, do not hesitate to reply to this email.\\n\\nWelcome to the family!\\n\\nBest regards",
      ),

      // 9. Error handler runCode
      n("9", "runCode", "Validate handoff completion and log errors", 1960, {
        runCodeDetails: {
          code: `const errors = [];
if (!variables.asana_project_id) errors.push('Asana project creation failed');
if (!variables.updated_deal_id) errors.push('HubSpot deal update failed');
if (!variables.slack_sent) errors.push('One or more Slack notifications failed');
output.handoff_status = errors.length === 0 ? 'complete' : 'partial';
output.error_log = errors.length > 0 ? errors.join('; ') : 'All handoff steps completed successfully';
output.handoff_summary = 'Quote #' + variables.quote_number + ' | ' + variables.customer_name + ' | $' + variables.quote_amount + ' | Status: ' + output.handoff_status;`,
          outputVariables: [
            { name: "handoff_status", type: "string" },
            { name: "error_log", type: "string" },
            { name: "handoff_summary", type: "string" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "5", "output_2"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1651. QUOTE SENT CONFIRMATION (7 nodes)
  // ===================================================================

  "Quote Sent Confirmation": {
    summary:
      "Receives a quote-sent webhook event, extracts quote details with runCode, checks for duplicate sends via condition, sends a confirmation email to the prospect, sends an SMS confirmation via Twilio, updates the sales rep activity log in HubSpot, and handles delivery failures with a condition branch.",
    nodes: [
      trigger("Webhook: Quote sent to prospect", "webhook_app", {
        source: "pandadoc",
        event: "document.sent",
        howItStarts:
          "Fires when a quote or proposal document is sent to a prospect via PandaDoc, initiating confirmation and tracking across channels",
        setup:
          "Configure PandaDoc webhook for document.sent events. Set Twilio API credentials for SMS confirmation. Ensure HubSpot API token has deal and activity write access.",
        inputFields: [],
      }),

      // 2. Quote details resolver runCode
      n(
        "2",
        "runCode",
        "Resolve quote details and detect duplicate sends",
        280,
        {
          runCodeDetails: {
            code: `const payload = JSON.parse(variables.webhook_payload || '{}');
const data = payload.data || payload;
const doc = data.document || data;
const recipient = (data.recipients || doc.recipients || [])[0] || {};
output.doc_id = doc.id || doc.document_id || '';
output.doc_name = doc.name || doc.title || '';
output.doc_status = doc.status || data.status || 'sent';
output.recipient_email = recipient.email || data.recipient_email || '';
output.recipient_name = (recipient.first_name || '') + ' ' + (recipient.last_name || '');
output.recipient_phone = recipient.phone || '';
output.sent_date = data.date_created || data.sent_at || new Date().toISOString();
output.doc_amount = doc.grand_total?.amount || doc.total || '';
output.deal_id = doc.metadata?.hubspot_deal_id || doc.external_id || '';
output.send_count = parseInt(data.send_count || data.version || '1');
output.is_duplicate = output.send_count > 1 ? 'yes' : 'no';
output.has_phone = output.recipient_phone ? 'yes' : 'no';`,
            outputVariables: [
              { name: "doc_id", type: "string" },
              { name: "doc_name", type: "string" },
              { name: "doc_status", type: "string" },
              { name: "recipient_email", type: "string" },
              { name: "recipient_name", type: "string" },
              { name: "recipient_phone", type: "string" },
              { name: "sent_date", type: "string" },
              { name: "doc_amount", type: "string" },
              { name: "deal_id", type: "string" },
              { name: "send_count", type: "number" },
              { name: "is_duplicate", type: "string" },
              { name: "has_phone", type: "string" },
            ],
          },
        },
      ),

      // 3. Duplicate send guard condition
      n("3", "condition", "Check if this is the first send attempt", 520, {
        conditionDetails: {
          conditions: [
            { variable: "is_duplicate", operator: "equals", value: "no" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 4. Confirmation email to prospect (True path — first send)
      emailNode(
        "4",
        "Send quote delivery confirmation to prospect",
        760,
        "{recipient_email}",
        "Your quote for {doc_name} is on its way",
        "Hi {recipient_name},\\n\\nThis is a quick confirmation that your personalized quote has been sent to you.\\n\\nQuote Details:\\n- Document: {doc_name}\\n- Amount: ${doc_amount}\\n- Sent: {sent_date}\\n\\nYou should receive the full document in your inbox shortly. Please review it at your convenience and let us know if you have any questions.\\n\\nWe look forward to hearing from you!\\n\\nBest regards",
      ),

      // 5. SMS confirmation Twilio
      smsNode(
        "5",
        "Send SMS quote delivery notification",
        1000,
        "{recipient_phone}",
        "Hi {recipient_name}, your quote for {doc_name} (${doc_amount}) has been sent to {recipient_email}. Check your inbox to review. Reply HELP for assistance.",
      ),

      // 6. Sales rep activity update HubSpot
      hubspotPostNode(
        "6",
        "Log quote sent activity to CRM deal",
        1240,
        "crm/v3/objects/deals/{deal_id}",
        [
          {
            key: "properties",
            value:
              '{"notes_last_contacted":"{sent_date}","notes_last_updated":"Quote sent: {doc_name} | Amount: ${doc_amount} | To: {recipient_name} ({recipient_email}) | Send count: {send_count}"}',
          },
        ],
        [
          { name: "id", customName: "updated_deal_id", type: "string" },
          {
            name: "properties.notes_last_contacted",
            customName: "contact_logged",
            type: "string",
          },
        ],
      ),

      // 7. Delivery failure handler condition
      n(
        "7",
        "condition",
        "Check if all confirmations delivered successfully",
        1480,
        {
          conditionDetails: {
            conditions: [
              { variable: "updated_deal_id", operator: "not_empty", value: "" },
            ],
            logicalOperator: "AND",
          },
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "6", "output_2"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1652. RFP OR PROCUREMENT INQUIRY FOLLOW-UP (9 nodes)
  // ===================================================================

  "RFP Procurement Inquiry Follow-Up": {
    summary:
      "Receives an RFP form submission via webhook, uses AI to analyze requirements, creates a CRM lead in HubSpot, routes internally based on deal size via condition, sends an immediate acknowledgment email, assembles a response document via Google Docs API, packages supporting materials via API, logs the response timeline to Sheets, and schedules a follow-up reminder with a delay.",
    nodes: [
      trigger("Webhook: RFP or procurement form submitted", "webhook_form", {
        source: "website",
        event: "form.rfp_submission",
        howItStarts:
          "Fires when a prospect submits an RFP, procurement inquiry, or vendor assessment form on the website, sending the raw form data including company details, requirements, budget range, and timeline",
        setup:
          "Configure form webhook for RFP submission events. Set OpenAI API key for requirements analysis. Ensure HubSpot API token has contact and deal create access. Configure Google Docs API for document assembly. Set up Google Sheets for timeline tracking.",
        inputFields: [],
      }),

      // 2. AI requirements analyzer
      aiNode(
        "2",
        "AI analyze RFP requirements and classify urgency",
        280,
        "You are a procurement and RFP analyst. Parse the raw form submission to extract company name, contact name, contact email, phone, company size, industry, specific requirements list, budget range, timeline urgency, evaluation criteria, and number of competing vendors. Classify the deal as enterprise (budget >$100K), mid_market ($25K-$100K), or smb (<$25K). Return JSON: {company_name, contact_name, contact_email, phone, company_size, industry, requirements_summary, budget_range, budget_tier, timeline, urgency_level, evaluation_criteria, competing_vendors, response_deadline}",
        "Raw RFP form submission: {webhook_payload}. Extract all procurement inquiry fields regardless of naming format.",
        [
          { field: "company_name", as: "rfp_company", type: "string" },
          { field: "contact_name", as: "rfp_contact", type: "string" },
          { field: "contact_email", as: "rfp_email", type: "string" },
          { field: "phone", as: "rfp_phone", type: "string" },
          { field: "company_size", as: "rfp_company_size", type: "string" },
          { field: "industry", as: "rfp_industry", type: "string" },
          {
            field: "requirements_summary",
            as: "rfp_requirements",
            type: "string",
          },
          { field: "budget_range", as: "rfp_budget", type: "string" },
          { field: "budget_tier", as: "budget_tier", type: "string" },
          { field: "timeline", as: "rfp_timeline", type: "string" },
          { field: "urgency_level", as: "rfp_urgency", type: "string" },
          { field: "evaluation_criteria", as: "eval_criteria", type: "string" },
          {
            field: "response_deadline",
            as: "response_deadline",
            type: "string",
          },
        ],
      ),

      // 3. CRM lead creation HubSpot
      hubspotPostNode(
        "3",
        "Create CRM lead and deal for RFP inquiry",
        520,
        "crm/v3/objects/deals",
        [
          {
            key: "properties",
            value:
              '{"dealname":"RFP: {rfp_company} — {rfp_industry}","amount":"{rfp_budget}","dealstage":"qualifiedtobuy","pipeline":"default","description":"RFP from {rfp_company} ({rfp_company_size} employees, {rfp_industry}). Requirements: {rfp_requirements}. Budget: {rfp_budget}. Timeline: {rfp_timeline}. Eval Criteria: {eval_criteria}. Competing Vendors: {competing_vendors}."}',
          },
        ],
        [
          { name: "id", customName: "rfp_deal_id", type: "string" },
          {
            name: "properties.dealname",
            customName: "rfp_deal_name",
            type: "string",
          },
        ],
      ),

      // 4. Internal routing engine condition
      n(
        "4",
        "condition",
        "Route based on budget tier for enterprise handling",
        760,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "budget_tier",
                operator: "equals",
                value: "enterprise",
              },
            ],
            logicalOperator: "AND",
          },
        },
      ),

      // 5. Immediate acknowledgment email
      emailNode(
        "5",
        "Send RFP receipt acknowledgment to prospect",
        1000,
        "{rfp_email}",
        "We received your RFP inquiry, {rfp_contact} — Next steps inside",
        "Dear {rfp_contact},\\n\\nThank you for submitting your procurement inquiry on behalf of {rfp_company}. We have received your requirements and our team is already reviewing them.\\n\\nWhat we understood:\\n- Requirements: {rfp_requirements}\\n- Budget Range: {rfp_budget}\\n- Timeline: {rfp_timeline}\\n- Your Deadline: {response_deadline}\\n\\nNext Steps:\\n1. A dedicated solutions architect will be assigned to your inquiry within 24 hours\\n2. We will prepare a comprehensive response addressing your evaluation criteria\\n3. Supporting materials and case studies will follow\\n\\nIf you need anything in the meantime, please reply directly to this email.\\n\\nBest regards",
      ),

      // 6. Document assembly Google Docs API
      n("6", "hitApi", "Assemble RFP response document via Google Docs", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://docs.googleapis.com/v1/documents",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "title",
              value: "RFP Response — {rfp_company} — {rfp_industry}",
            },
          ],
          variables: [
            { name: "documentId", customName: "rfp_doc_id", type: "string" },
            { name: "title", customName: "rfp_doc_title", type: "string" },
            {
              name: "revisionId",
              customName: "rfp_doc_revision",
              type: "string",
            },
          ],
        },
      }),

      // 7. Supporting materials package API
      n("7", "hitApi", "Retrieve relevant case studies and materials", 1480, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubapi.com/cms/v3/blogs/posts?state=PUBLISHED&limit=3&property=name,url,meta_description&name__contains={rfp_industry}",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "total", customName: "materials_count", type: "number" },
            {
              name: "results[0].name",
              customName: "case_study_1_title",
              type: "string",
            },
            {
              name: "results[0].url",
              customName: "case_study_1_url",
              type: "string",
            },
            {
              name: "results[1].name",
              customName: "case_study_2_title",
              type: "string",
            },
            {
              name: "results[1].url",
              customName: "case_study_2_url",
              type: "string",
            },
          ],
        },
      }),

      // 8. Response timeline tracker Sheets
      sheetsNode(
        "8",
        "Log RFP to response timeline tracker",
        1720,
        "RFP_Tracker",
        `[["${new Date().toISOString()}", "{rfp_company}", "{rfp_contact}", "{rfp_email}", "{rfp_industry}", "{rfp_budget}", "{budget_tier}", "{rfp_timeline}", "{response_deadline}", "{rfp_urgency}", "{rfp_deal_id}", "{rfp_doc_id}", "in_progress"]]`,
      ),

      // 9. Follow-up reminder scheduler delay
      n(
        "9",
        "delayAction",
        "Schedule follow-up reminder for response deadline",
        1960,
        {
          delayDetails: { duration: 3, unit: "days" },
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1653. ROI CALCULATOR COMPLETION FOLLOW-UP (8 nodes)
  // ===================================================================

  "ROI Calculator Completion Follow-Up": {
    summary:
      "Receives a webhook when a prospect completes an ROI calculator, computes final ROI results with runCode, updates the lead score in HubSpot, uses AI to craft a personalized results email, generates a PDF report link with runCode, waits 48 hours to monitor demo booking, sends a no-book follow-up email, and notifies the sales rep via Slack.",
    nodes: [
      trigger("Webhook: ROI calculator submission completed", "webhook_form", {
        source: "website",
        event: "form.roi_calculator_completed",
        howItStarts:
          "Fires when a prospect completes the interactive ROI calculator on the website, submitting their inputs and receiving calculated results including projected savings, payback period, and ROI percentage",
        setup:
          "Configure form webhook for ROI calculator completion. Set up HubSpot API token for lead scoring. Configure Slack webhook for sales notifications.",
        inputFields: [],
      }),

      // 2. ROI results computation runCode
      n("2", "runCode", "Compute and validate ROI calculator results", 280, {
        runCodeDetails: {
          code: `const payload = JSON.parse(variables.webhook_payload || '{}');
const data = payload.data || payload;
output.contact_email = data.email || data.contact_email || '';
output.contact_name = data.name || data.first_name + ' ' + (data.last_name || '') || '';
output.company_name = data.company || data.company_name || '';
output.company_size = data.employees || data.team_size || '';
output.current_spend = parseFloat(data.current_spend || data.annual_cost || '0');
output.projected_savings = parseFloat(data.projected_savings || '0');
output.roi_percentage = output.current_spend > 0 ? Math.round((output.projected_savings / output.current_spend) * 100) : 0;
output.payback_months = output.projected_savings > 0 ? Math.round((output.current_spend / (output.projected_savings / 12))) : 0;
output.annual_roi = (output.projected_savings - output.current_spend).toFixed(2);
output.five_year_value = (output.projected_savings * 5).toFixed(2);
output.lead_score_boost = output.roi_percentage > 200 ? 30 : output.roi_percentage > 100 ? 20 : 10;
output.tier = output.current_spend > 100000 ? 'enterprise' : output.current_spend > 25000 ? 'mid_market' : 'smb';`,
          outputVariables: [
            { name: "contact_email", type: "string" },
            { name: "contact_name", type: "string" },
            { name: "company_name", type: "string" },
            { name: "company_size", type: "string" },
            { name: "current_spend", type: "number" },
            { name: "projected_savings", type: "number" },
            { name: "roi_percentage", type: "number" },
            { name: "payback_months", type: "number" },
            { name: "annual_roi", type: "string" },
            { name: "five_year_value", type: "string" },
            { name: "lead_score_boost", type: "number" },
            { name: "tier", type: "string" },
          ],
        },
      }),

      // 3. Lead scoring update HubSpot
      hubspotPostNode(
        "3",
        "Update lead score in HubSpot based on ROI results",
        520,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{contact_email}","company":"{company_name}","numberofemployees":"{company_size}","hs_lead_status":"OPEN","notes_last_updated":"ROI Calculator completed. ROI: {roi_percentage}%. Savings: ${projected_savings}. Payback: {payback_months} months. Tier: {tier}. Score boost: +{lead_score_boost}"}',
          },
        ],
        [
          { name: "id", customName: "contact_id", type: "string" },
          {
            name: "properties.hs_lead_status",
            customName: "lead_status",
            type: "string",
          },
        ],
      ),

      // 4. AI personalized results email
      aiNode(
        "4",
        "AI craft personalized ROI results email",
        760,
        "You are a value-selling specialist. Using the ROI calculator results, craft a highly personalized email that reinforces the value story. Highlight the most impressive metric (ROI %, payback period, or 5-year savings). Reference the company size and tier to adjust language appropriately. Include a subtle demo CTA. Return JSON: {subject_line, headline, key_metric_callout, value_narrative, demo_cta, ps_line}",
        "Contact: {contact_name} at {company_name} ({company_size} employees, {tier}) | ROI: {roi_percentage}% | Savings: ${projected_savings}/yr | Payback: {payback_months} months | 5yr Value: ${five_year_value} | Current Spend: ${current_spend}",
        [
          { field: "subject_line", as: "roi_subject", type: "string" },
          { field: "headline", as: "roi_headline", type: "string" },
          { field: "key_metric_callout", as: "key_metric", type: "string" },
          { field: "value_narrative", as: "value_narrative", type: "string" },
          { field: "demo_cta", as: "demo_cta", type: "string" },
          { field: "ps_line", as: "roi_ps", type: "string" },
        ],
      ),

      // 5. PDF report generation runCode
      n(
        "5",
        "runCode",
        "Generate PDF report download link",
        520,
        {
          runCodeDetails: {
            code: `const reportId = 'roi_' + variables.contact_email.replace('@', '_at_').replace(/\\./g, '_') + '_' + Date.now();
output.report_url = 'https://app.yourcompany.com/reports/roi/' + reportId;
output.report_id = reportId;
output.report_summary = 'ROI Report for ' + variables.company_name + ': ' + variables.roi_percentage + '% ROI, $' + variables.projected_savings + ' annual savings, ' + variables.payback_months + ' month payback';`,
            outputVariables: [
              { name: "report_url", type: "string" },
              { name: "report_id", type: "string" },
              { name: "report_summary", type: "string" },
            ],
          },
        },
        400,
      ),

      // 6. Demo booking CTA monitor delay 48hrs
      n(
        "6",
        "delayAction",
        "Wait 48 hours to monitor demo booking activity",
        1000,
        {
          delayDetails: { duration: 48, unit: "hours" },
        },
      ),

      // 7. No-book follow-up email
      emailNode(
        "7",
        "Send follow-up email with ROI report and demo CTA",
        1240,
        "{contact_email}",
        "{roi_subject}",
        "Hi {contact_name},\\n\\n{roi_headline}\\n\\n{key_metric}\\n\\n{value_narrative}\\n\\nYour personalized ROI report is ready: {report_url}\\n\\nKey highlights from your analysis:\\n- Projected annual savings: ${projected_savings}\\n- ROI: {roi_percentage}%\\n- Payback period: {payback_months} months\\n- 5-year total value: ${five_year_value}\\n\\n{demo_cta}\\n\\n{roi_ps}\\n\\nBest regards",
      ),

      // 8. Sales rep notification Slack
      slackNode(
        "8",
        "Notify sales rep of ROI calculator completion",
        1480,
        "📊 *ROI Calculator Completed*\\n*Prospect:* {contact_name} ({contact_email})\\n*Company:* {company_name} ({company_size} employees) — {tier}\\n*ROI:* {roi_percentage}% | *Savings:* ${projected_savings}/yr\\n*Payback:* {payback_months} months | *5yr Value:* ${five_year_value}\\n*Current Spend:* ${current_spend}\\n*Lead Score Boost:* +{lead_score_boost}\\n*Report:* {report_url}\\n*Status:* Follow-up email sent, awaiting demo booking",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1654. ROI CALCULATOR FOLLOW-UP TO MEETING (8 nodes)
  // ===================================================================

  "ROI Calculator Follow-Up to Meeting": {
    summary:
      "Receives a webhook when a meeting is completed, extracts meeting notes via Gong API, uses AI to build a personalized ROI model from discussion points, generates an interactive calculator link with runCode, sends a personalized follow-up email, waits 5 days to track calculator engagement, branches on engagement level, and syncs activity back to HubSpot CRM.",
    nodes: [
      trigger("Webhook: Sales meeting completed", "webhook_app", {
        source: "calendly",
        event: "invitee.completed",
        howItStarts:
          "Fires when a scheduled sales meeting is marked as completed, initiating the post-meeting ROI follow-up sequence with personalized calculator and content",
        setup:
          "Configure Calendly webhook for invitee.completed events. Set Gong API credentials for transcript retrieval. Ensure HubSpot API token has contact and deal read/write access.",
        inputFields: [],
      }),

      // 2. Meeting notes extraction Gong API
      n("2", "hitApi", "Retrieve meeting transcript and notes from Gong", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.gong.io/v2/calls/transcript",
          headers: [
            { key: "Authorization", value: "Bearer {gong_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filter",
              value:
                '{"fromDateTime":"{meeting_start_time}","toDateTime":"{meeting_end_time}"}',
            },
          ],
          variables: [
            {
              name: "callTranscripts[0].transcript[0].sentences",
              customName: "transcript_text",
              type: "string",
            },
            {
              name: "callTranscripts[0].callId",
              customName: "gong_call_id",
              type: "string",
            },
            {
              name: "callTranscripts[0].transcript[0].speakerName",
              customName: "primary_speaker",
              type: "string",
            },
          ],
        },
      }),

      // 3. AI ROI model builder
      aiNode(
        "3",
        "AI build personalized ROI model from meeting discussion",
        520,
        "You are a sales engineer who builds custom ROI models. From the meeting transcript, identify the prospect pain points, current tools and costs discussed, specific use cases mentioned, team size, decision criteria, and budget signals. Build a personalized ROI calculation framework. Extract the prospect name, email, company, and key financial data points. Return JSON: {prospect_name, prospect_email, company_name, current_annual_cost, identified_savings_areas, estimated_savings_pct, custom_roi_inputs, key_pain_points, use_cases, team_size, decision_timeline, budget_signal}",
        "Meeting transcript: {transcript_text}. Extract all financial and ROI-relevant data points from the conversation regardless of how they were discussed.",
        [
          { field: "prospect_name", as: "prospect_name", type: "string" },
          { field: "prospect_email", as: "prospect_email", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "current_annual_cost", as: "current_cost", type: "string" },
          {
            field: "identified_savings_areas",
            as: "savings_areas",
            type: "string",
          },
          { field: "estimated_savings_pct", as: "savings_pct", type: "string" },
          { field: "custom_roi_inputs", as: "roi_inputs", type: "string" },
          { field: "key_pain_points", as: "pain_points", type: "string" },
          { field: "use_cases", as: "use_cases", type: "string" },
          { field: "team_size", as: "team_size", type: "string" },
          {
            field: "decision_timeline",
            as: "decision_timeline",
            type: "string",
          },
          { field: "budget_signal", as: "budget_signal", type: "string" },
        ],
      ),

      // 4. Interactive calculator link generator runCode
      n(
        "4",
        "runCode",
        "Generate personalized interactive ROI calculator link",
        760,
        {
          runCodeDetails: {
            code: `const params = new URLSearchParams({
  name: variables.prospect_name || '',
  company: variables.company_name || '',
  cost: variables.current_cost || '0',
  team: variables.team_size || '',
  savings: variables.savings_pct || '20',
  areas: variables.savings_areas || '',
  ref: variables.gong_call_id || ''
});
output.calculator_url = 'https://app.yourcompany.com/roi-calculator?' + params.toString();
output.calculator_id = 'calc_' + (variables.prospect_email || '').replace('@', '_') + '_' + Date.now();
const costNum = parseFloat(variables.current_cost || '0');
const savPct = parseFloat(variables.savings_pct || '20');
output.estimated_annual_savings = (costNum * savPct / 100).toFixed(2);
output.estimated_roi_pct = savPct > 0 ? Math.round(savPct * 2.5) : 0;`,
            outputVariables: [
              { name: "calculator_url", type: "string" },
              { name: "calculator_id", type: "string" },
              { name: "estimated_annual_savings", type: "string" },
              { name: "estimated_roi_pct", type: "number" },
            ],
          },
        },
      ),

      // 5. Personalized follow-up email
      emailNode(
        "5",
        "Send post-meeting follow-up with personalized ROI calculator",
        1000,
        "{prospect_email}",
        "Following up on our conversation, {prospect_name} — Your custom ROI model is ready",
        "Hi {prospect_name},\\n\\nThank you for taking the time to meet with us about {company_name}'s needs.\\n\\nBased on our discussion, I have put together a personalized ROI calculator that addresses the specific areas we talked about:\\n\\nKey Pain Points We Discussed:\\n{pain_points}\\n\\nUse Cases Identified:\\n{use_cases}\\n\\nPreliminary Estimates:\\n- Current annual cost: ${current_cost}\\n- Estimated savings: ${estimated_annual_savings}/year ({savings_pct}%)\\n- Projected ROI: {estimated_roi_pct}%\\n\\nTry your personalized calculator here: {calculator_url}\\n\\nYou can adjust the inputs to model different scenarios based on your {team_size}-person team.\\n\\nLooking forward to reviewing the results together.\\n\\nBest regards",
      ),

      // 6. Calculator engagement tracker delay 5 days
      n(
        "6",
        "delayAction",
        "Wait 5 days for calculator engagement tracking",
        1240,
        {
          delayDetails: { duration: 5, unit: "days" },
        },
      ),

      // 7. Engagement-based branching condition
      n(
        "7",
        "condition",
        "Check if budget signal indicates active evaluation",
        1480,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "budget_signal",
                operator: "not_equals",
                value: "none",
              },
            ],
            logicalOperator: "AND",
          },
        },
      ),

      // 8a. CRM activity sync HubSpot (True path — active buyer)
      hubspotPostNode(
        "8",
        "Sync ROI follow-up and engagement to CRM",
        1720,
        "crm/v3/objects/contacts/{prospect_email}",
        [
          {
            key: "properties",
            value:
              '{"notes_last_updated":"Post-meeting ROI follow-up sent. Calculator: {calculator_url}. Estimated savings: ${estimated_annual_savings} ({savings_pct}%). Pain points: {pain_points}. Decision timeline: {decision_timeline}. Budget signal: {budget_signal}. Use cases: {use_cases}.","hs_lead_status":"IN_PROGRESS"}',
          },
        ],
        [
          { name: "id", customName: "updated_contact_id", type: "string" },
          {
            name: "properties.hs_lead_status",
            customName: "updated_status",
            type: "string",
          },
        ],
      ),

      // 8b. CRM activity sync HubSpot (False path — nurture)
      hubspotPostNode(
        "9",
        "Log follow-up sent for nurture tracking",
        1720,
        "crm/v3/objects/contacts/{prospect_email}",
        [
          {
            key: "properties",
            value:
              '{"notes_last_updated":"Post-meeting ROI calculator sent. No active budget signal. Continue nurture. Calculator: {calculator_url}. Savings estimate: ${estimated_annual_savings}."}',
          },
        ],
        [{ name: "id", customName: "nurture_contact_id", type: "string" }],
        400,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("7", "9", "output_2"),
    ],
  },

  // ===================================================================
  // 1655. REAL ESTATE LEAD NURTURE DRIP (10 nodes)
  // ===================================================================

  "Real Estate Lead Nurture Drip": {
    summary:
      "Captures a real estate lead via webhook, enriches data via Clearbit API, uses AI to match property preferences, sends a Day 0 welcome email, waits 3 days, sends Day 3 market insights, branches on engagement scoring, sends Day 7 featured listings to engaged leads, sends Day 14 neighborhood guide, and alerts the agent via SMS and Slack for hot leads.",
    nodes: [
      trigger("Webhook: Real estate lead captured", "webhook_form", {
        source: "website",
        event: "form.real_estate_lead",
        howItStarts:
          "Fires when a prospect submits a real estate inquiry form — property search, home valuation request, or open house registration — sending the raw lead capture data",
        setup:
          "Configure form webhook for real estate lead submissions. Set Clearbit API key for lead enrichment. Configure HubSpot or CRM for lead storage. Set up Slack webhook for agent alerts.",
        inputFields: [],
      }),

      // 2. Lead enrichment Clearbit API
      n("2", "hitApi", "Enrich real estate lead via Clearbit", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://person.clearbit.com/v2/people/find?email={lead_email}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "name.fullName",
              customName: "enriched_name",
              type: "string",
            },
            {
              name: "employment.title",
              customName: "job_title",
              type: "string",
            },
            {
              name: "employment.name",
              customName: "employer_name",
              type: "string",
            },
            {
              name: "employment.seniority",
              customName: "seniority",
              type: "string",
            },
            {
              name: "location",
              customName: "enriched_location",
              type: "string",
            },
            { name: "geo.city", customName: "city", type: "string" },
          ],
        },
      }),

      // 3. AI property match analyzer
      aiNode(
        "3",
        "AI analyze property preferences and match score",
        520,
        "You are a real estate lead analyst. From the raw form submission and enriched data, extract the lead email, name, phone, preferred property type (house, condo, townhouse), budget range, preferred neighborhoods, number of bedrooms, move-in timeline, pre-approval status, and any special requirements. Assess lead quality as hot (pre-approved, near-term timeline), warm (researching, 3-6 month timeline), or cold (long-term, just browsing). Return JSON: {lead_email, lead_name, lead_phone, property_type, budget_min, budget_max, neighborhoods, bedrooms, move_timeline, pre_approved, special_requirements, lead_quality, recommended_listings_type}",
        "Raw form data: {webhook_payload}. Enriched data: Name={enriched_name}, Title={job_title}, Employer={employer_name}, Location={enriched_location}, City={city}. Extract all real estate lead preferences regardless of field naming.",
        [
          { field: "lead_email", as: "lead_email", type: "string" },
          { field: "lead_name", as: "lead_name", type: "string" },
          { field: "lead_phone", as: "lead_phone", type: "string" },
          { field: "property_type", as: "property_type", type: "string" },
          { field: "budget_min", as: "budget_min", type: "string" },
          { field: "budget_max", as: "budget_max", type: "string" },
          { field: "neighborhoods", as: "neighborhoods", type: "string" },
          { field: "bedrooms", as: "bedrooms", type: "string" },
          { field: "move_timeline", as: "move_timeline", type: "string" },
          { field: "pre_approved", as: "pre_approved", type: "string" },
          { field: "lead_quality", as: "lead_quality", type: "string" },
          {
            field: "recommended_listings_type",
            as: "listings_type",
            type: "string",
          },
        ],
      ),

      // 4. Day 0 welcome email
      emailNode(
        "4",
        "Send Day 0 welcome and property search summary",
        760,
        "{lead_email}",
        "Welcome {lead_name} — Your property search starts here",
        "Hi {lead_name},\\n\\nThank you for reaching out about your property search! We are excited to help you find your perfect home.\\n\\nHere is what we have on file for your preferences:\\n- Property Type: {property_type}\\n- Budget: ${budget_min} – ${budget_max}\\n- Preferred Areas: {neighborhoods}\\n- Bedrooms: {bedrooms}\\n- Timeline: {move_timeline}\\n\\nWhat happens next:\\n1. We will curate personalized listings matching your criteria\\n2. You will receive market insights for your preferred neighborhoods\\n3. A dedicated agent will be in touch for a personalized consultation\\n\\nIn the meantime, feel free to reply to this email with any questions or updated preferences.\\n\\nHappy house hunting!\\n\\nBest regards",
      ),

      // 5. Delay 3 days
      n("5", "delayAction", "Wait 3 days before market insights email", 1000, {
        delayDetails: { duration: 3, unit: "days" },
      }),

      // 6. Day 3 market insights email
      emailNode(
        "6",
        "Send Day 3 local market insights email",
        1240,
        "{lead_email}",
        "Market update for {neighborhoods}, {lead_name}",
        "Hi {lead_name},\\n\\nHere is your personalized market update for the {neighborhoods} area.\\n\\nMarket Snapshot:\\n- Property type demand: {property_type} inventory is moving quickly in your price range\\n- Budget range: ${budget_min} – ${budget_max} is competitive for {bedrooms}-bedroom homes\\n- Timeline insight: With your {move_timeline} timeline, we recommend starting viewings soon\\n\\nPro Tips for Your Search:\\n1. Properties in your range typically receive offers within 2-3 weeks of listing\\n2. Pre-approval status can significantly strengthen your offer position\\n3. Consider expanding your search to adjacent neighborhoods for more options\\n\\nWe are tracking new listings daily and will notify you of any perfect matches.\\n\\nReply anytime with questions!\\n\\nBest regards",
      ),

      // 7. Engagement scoring condition
      n(
        "7",
        "condition",
        "Check if lead quality is hot for accelerated nurture",
        1480,
        {
          conditionDetails: {
            conditions: [
              { variable: "lead_quality", operator: "equals", value: "hot" },
            ],
            logicalOperator: "AND",
          },
        },
      ),

      // 8. Day 7 featured listings email (True path — hot leads get accelerated)
      emailNode(
        "8",
        "Send Day 7 featured listings for hot leads",
        1720,
        "{lead_email}",
        "Hot new listings matching your criteria, {lead_name}",
        "Hi {lead_name},\\n\\nWe have curated a selection of featured listings that match your exact criteria:\\n\\nYour Search Criteria:\\n- {property_type} in {neighborhoods}\\n- {bedrooms} bedrooms, ${budget_min} – ${budget_max}\\n\\nFeatured Listings This Week:\\nWe have identified several properties that check all your boxes. Based on current market velocity, these listings may not last long.\\n\\nSince you are pre-approved and looking to move within {move_timeline}, I would love to schedule private viewings for any that catch your eye.\\n\\nShall we set up a tour this week? Reply with your preferred days and times.\\n\\nBest regards",
      ),

      // 9. Day 14 neighborhood guide email
      emailNode(
        "9",
        "Send Day 14 comprehensive neighborhood guide",
        1960,
        "{lead_email}",
        "Your complete guide to living in {neighborhoods}",
        "Hi {lead_name},\\n\\nAs you continue exploring {neighborhoods}, we put together a comprehensive neighborhood guide just for you.\\n\\nInside Your Guide:\\n- School ratings and district boundaries\\n- Average commute times from {neighborhoods}\\n- Local dining, shopping, and entertainment highlights\\n- Parks, recreation, and community amenities\\n- Property value trends over the past 5 years\\n- Upcoming neighborhood developments\\n\\nWhy {neighborhoods}?\\nWith your preference for {bedrooms}-bedroom {property_type} properties in the ${budget_min} – ${budget_max} range, this area offers excellent value and quality of life.\\n\\nReady to see properties in person? Let us schedule your neighborhood tour.\\n\\nBest regards",
        400,
      ),

      // 10. Hot lead alert SMS + Slack
      slackNode(
        "10",
        "Alert agent via Slack and SMS for hot lead",
        2200,
        "🏠 *Hot Real Estate Lead Alert*\\n*Lead:* {lead_name} ({lead_email})\\n*Phone:* {lead_phone}\\n*Quality:* {lead_quality} — {pre_approved} pre-approval\\n*Preferences:* {property_type}, {bedrooms}BR, ${budget_min}-${budget_max}\\n*Areas:* {neighborhoods}\\n*Timeline:* {move_timeline}\\n*Employer:* {employer_name} ({job_title})\\n*Action:* Schedule personal consultation ASAP",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("7", "9", "output_2"),
      e("8", "9"),
      e("9", "10"),
    ],
  },

  // ===================================================================
  // 1656. REFERENCE REQUEST WORKFLOW (9 nodes)
  // ===================================================================

  "Reference Request Workflow": {
    summary:
      "Receives a reference request via webhook, analyzes the prospect profile with runCode, uses AI to match the best reference customers, checks reference availability in HubSpot, sends a reference outreach email, tracks response via condition, sends a warm introduction email through an approval gate, briefs the sales rep via Slack, and logs to the reference program tracker in Sheets.",
    nodes: [
      trigger("Webhook: Sales rep requests customer reference", "webhook_app", {
        source: "internal",
        event: "reference.requested",
        howItStarts:
          "Fires when a sales rep submits a reference request through the internal tool, specifying the prospect details, deal context, and the type of reference needed",
        setup:
          "Configure internal tool webhook for reference requests. Set up HubSpot API token for customer data lookup. Configure approval gate for reference introductions. Set up Google Sheets for reference program tracking.",
        inputFields: [],
      }),

      // 2. Prospect profile analyzer runCode
      n(
        "2",
        "runCode",
        "Analyze prospect profile for reference matching",
        280,
        {
          runCodeDetails: {
            code: `const payload = JSON.parse(variables.webhook_payload || '{}');
const data = payload.data || payload;
output.rep_email = data.rep_email || data.requested_by || '';
output.rep_name = data.rep_name || '';
output.prospect_name = data.prospect_name || data.prospect_company || '';
output.prospect_email = data.prospect_email || '';
output.prospect_industry = data.industry || '';
output.prospect_size = data.company_size || '';
output.deal_id = data.deal_id || '';
output.deal_amount = data.deal_amount || '';
output.deal_stage = data.deal_stage || '';
output.use_case = data.use_case || data.reference_type || '';
output.specific_concerns = data.concerns || data.objections || '';
output.urgency = data.urgency || 'standard';
output.match_criteria = output.prospect_industry + '|' + output.prospect_size + '|' + output.use_case;`,
            outputVariables: [
              { name: "rep_email", type: "string" },
              { name: "rep_name", type: "string" },
              { name: "prospect_name", type: "string" },
              { name: "prospect_email", type: "string" },
              { name: "prospect_industry", type: "string" },
              { name: "prospect_size", type: "string" },
              { name: "deal_id", type: "string" },
              { name: "deal_amount", type: "string" },
              { name: "deal_stage", type: "string" },
              { name: "use_case", type: "string" },
              { name: "specific_concerns", type: "string" },
              { name: "urgency", type: "string" },
              { name: "match_criteria", type: "string" },
            ],
          },
        },
      ),

      // 3. AI reference matcher
      aiNode(
        "3",
        "AI match best reference customers for this prospect",
        520,
        "You are a customer success manager who manages the reference program. Given the prospect profile and match criteria, recommend the best reference customers. Consider industry match, company size similarity, use case alignment, and the specific concerns the prospect has. Return JSON: {reference_name, reference_email, reference_company, reference_industry, reference_use_case, match_score, match_reasoning, talking_points, prep_notes}",
        "Prospect: {prospect_name} ({prospect_industry}, {prospect_size} employees) | Use Case: {use_case} | Concerns: {specific_concerns} | Deal: ${deal_amount} at {deal_stage} | Match Criteria: {match_criteria}",
        [
          { field: "reference_name", as: "reference_name", type: "string" },
          { field: "reference_email", as: "reference_email", type: "string" },
          {
            field: "reference_company",
            as: "reference_company",
            type: "string",
          },
          {
            field: "reference_industry",
            as: "reference_industry",
            type: "string",
          },
          {
            field: "reference_use_case",
            as: "reference_use_case",
            type: "string",
          },
          { field: "match_score", as: "match_score", type: "string" },
          { field: "match_reasoning", as: "match_reasoning", type: "string" },
          { field: "talking_points", as: "talking_points", type: "string" },
          { field: "prep_notes", as: "prep_notes", type: "string" },
        ],
      ),

      // 4. Reference availability check HubSpot
      hubspotGetNode(
        "4",
        "Check reference customer availability in HubSpot",
        760,
        "crm/v3/objects/contacts/{reference_email}?properties=email,firstname,lastname,hs_lead_status,notes_last_updated,num_associated_deals,hs_email_last_open_date",
        [
          { name: "id", customName: "reference_contact_id", type: "string" },
          {
            name: "properties.firstname",
            customName: "ref_first_name",
            type: "string",
          },
          {
            name: "properties.lastname",
            customName: "ref_last_name",
            type: "string",
          },
          {
            name: "properties.hs_lead_status",
            customName: "ref_status",
            type: "string",
          },
          {
            name: "properties.notes_last_updated",
            customName: "ref_last_activity",
            type: "string",
          },
          {
            name: "properties.hs_email_last_open_date",
            customName: "ref_last_email_open",
            type: "string",
          },
        ],
      ),

      // 5. Reference customer outreach email
      emailNode(
        "5",
        "Send reference participation request email",
        1000,
        "{reference_email}",
        "Quick favor, {ref_first_name}? A prospect would love to hear about your experience",
        "Hi {ref_first_name},\\n\\nI hope you are doing well! We have a prospect ({prospect_name} in {prospect_industry}) who is evaluating our solution for a similar use case to yours — {reference_use_case}.\\n\\nThey have some specific questions about {specific_concerns}, and given your experience at {reference_company}, I think you would be the perfect person to share insights.\\n\\nWould you be open to a brief 15-20 minute call with them? We would handle all the scheduling and preparation.\\n\\nTalking points (just suggestions):\\n{talking_points}\\n\\nNo pressure at all — just let me know if you are available and we will take care of the rest.\\n\\nThank you for being such a great customer!\\n\\nBest regards",
      ),

      // 6. Response tracking condition
      n(
        "6",
        "condition",
        "Check if reference customer is an active customer",
        1240,
        {
          conditionDetails: {
            conditions: [
              { variable: "ref_status", operator: "equals", value: "CUSTOMER" },
            ],
            logicalOperator: "AND",
          },
        },
      ),

      // 7. Warm introduction email (approval gate) — True path
      n(
        "7",
        "approvalGate",
        "Approval gate: Confirm reference introduction",
        1480,
        {
          approvalGateDetails: {
            approver: "{rep_email}",
            message:
              "Reference match ready for {prospect_name}. Reference: {ref_first_name} {ref_last_name} at {reference_company} (Match Score: {match_score}). Reasoning: {match_reasoning}. Approve to send warm introduction email.",
            approveAction: "Send warm introduction email",
            rejectAction: "Cancel introduction and find alternative",
          },
        },
      ),

      // 8. Sales rep briefing Slack
      slackNode(
        "8",
        "Brief sales rep on reference match and prep notes",
        1720,
        "📋 *Reference Match Ready*\\n*Prospect:* {prospect_name} ({prospect_industry})\\n*Deal:* ${deal_amount} — {deal_stage}\\n*Reference:* {ref_first_name} {ref_last_name} at {reference_company}\\n*Match Score:* {match_score}/100 — {match_reasoning}\\n*Use Case Alignment:* {reference_use_case}\\n*Concerns to Address:* {specific_concerns}\\n*Prep Notes:* {prep_notes}\\n*Talking Points:* {talking_points}\\n*Status:* Outreach sent, awaiting reference confirmation",
      ),

      // 9. Reference program tracking Sheets
      sheetsNode(
        "9",
        "Log reference request to program tracker",
        1960,
        "ReferenceProgram",
        `[["${new Date().toISOString()}", "{rep_name}", "{prospect_name}", "{prospect_industry}", "{deal_amount}", "{deal_stage}", "{ref_first_name} {ref_last_name}", "{reference_company}", "{match_score}", "{use_case}", "{urgency}", "outreach_sent"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "9", "output_2"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1657. RENEWAL OPPORTUNITY CREATION (8 nodes)
  // ===================================================================

  "Renewal Opportunity Creation": {
    summary:
      "Runs on a schedule 90 days before renewal, fetches account health scores from HubSpot, uses AI to generate a usage value summary, creates a renewal opportunity in HubSpot, branches on health score for routing, sends a renewal proposal email, alerts the CSM and AE via Slack, and schedules a follow-up sequence with a delay.",
    nodes: [
      trigger("Schedule: 90-day pre-renewal opportunity scanner", "schedule", {
        schedule: "Daily at 8:00 AM",
        howItStarts:
          "Runs daily to scan for accounts with renewal dates approximately 90 days out, creating renewal opportunities and initiating the renewal engagement sequence",
        setup:
          "Configure daily schedule trigger. Ensure HubSpot API token has deal and company read/write access. Configure Slack webhook for CSM and AE notifications.",
        inputFields: [],
      }),

      // 2. Account health score fetch HubSpot
      hubspotGetNode(
        "2",
        "Fetch accounts approaching renewal from HubSpot",
        280,
        "crm/v3/objects/deals/search",
        [
          { name: "results[0].id", customName: "deal_id", type: "string" },
          {
            name: "results[0].properties.dealname",
            customName: "deal_name",
            type: "string",
          },
          {
            name: "results[0].properties.amount",
            customName: "deal_amount",
            type: "string",
          },
          {
            name: "results[0].properties.closedate",
            customName: "renewal_date",
            type: "string",
          },
          {
            name: "results[0].properties.hubspot_owner_id",
            customName: "csm_owner_id",
            type: "string",
          },
          {
            name: "results[0].properties.hs_deal_stage_probability",
            customName: "health_score",
            type: "string",
          },
          {
            name: "results[0].properties.pipeline",
            customName: "pipeline",
            type: "string",
          },
          { name: "total", customName: "total_renewals", type: "number" },
        ],
      ),

      // 3. AI usage value summary generator
      aiNode(
        "3",
        "AI generate personalized usage value summary",
        520,
        "You are a customer success analyst preparing renewal summaries. Given the account details and health score, create a compelling value summary that highlights what the customer has achieved, key metrics, and the business impact of continuing the relationship. Tailor the tone based on health score: high health (celebratory), medium (reinforcing value), low (addressing concerns). Return JSON: {customer_name, customer_email, value_headline, key_achievements, usage_highlights, business_impact, renewal_recommendation, risk_level, expansion_opportunity, personalized_message}",
        "Deal: {deal_name} | Amount: ${deal_amount} | Renewal Date: {renewal_date} | Health Score: {health_score}% | Pipeline: {pipeline} | Total renewals in batch: {total_renewals}",
        [
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "value_headline", as: "value_headline", type: "string" },
          { field: "key_achievements", as: "key_achievements", type: "string" },
          { field: "usage_highlights", as: "usage_highlights", type: "string" },
          { field: "business_impact", as: "business_impact", type: "string" },
          {
            field: "renewal_recommendation",
            as: "renewal_recommendation",
            type: "string",
          },
          { field: "risk_level", as: "risk_level", type: "string" },
          {
            field: "expansion_opportunity",
            as: "expansion_opportunity",
            type: "string",
          },
          {
            field: "personalized_message",
            as: "personalized_message",
            type: "string",
          },
        ],
      ),

      // 4. Renewal opportunity creation HubSpot
      hubspotPostNode(
        "4",
        "Create renewal opportunity in HubSpot CRM",
        760,
        "crm/v3/objects/deals",
        [
          {
            key: "properties",
            value:
              '{"dealname":"Renewal: {deal_name}","amount":"{deal_amount}","dealstage":"appointmentscheduled","pipeline":"renewals","closedate":"{renewal_date}","description":"Auto-created renewal opportunity. Health: {health_score}%. Risk: {risk_level}. Expansion: {expansion_opportunity}. Recommendation: {renewal_recommendation}."}',
          },
        ],
        [
          { name: "id", customName: "renewal_deal_id", type: "string" },
          {
            name: "properties.dealname",
            customName: "renewal_deal_name",
            type: "string",
          },
        ],
      ),

      // 5. Health-based routing condition
      n("5", "condition", "Route based on account health score", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "risk_level", operator: "equals", value: "low" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 6. Renewal proposal email
      emailNode(
        "6",
        "Send renewal proposal and value summary email",
        1240,
        "{customer_email}",
        "{value_headline} — Your renewal is coming up, {customer_name}",
        "Hi {customer_name},\\n\\n{personalized_message}\\n\\nYour Partnership Highlights:\\n{key_achievements}\\n\\nUsage Summary:\\n{usage_highlights}\\n\\nBusiness Impact:\\n{business_impact}\\n\\nYour renewal date is {renewal_date}, and we want to make sure the transition is seamless.\\n\\nRenewal Details:\\n- Current plan amount: ${deal_amount}\\n- Renewal recommendation: {renewal_recommendation}\\n- Expansion opportunity: {expansion_opportunity}\\n\\nI would love to schedule a quick call to discuss your renewal and explore how we can deliver even more value in the next term.\\n\\nBest regards",
      ),

      // 7. CSM AE alert Slack
      slackNode(
        "7",
        "Alert CSM and AE of renewal opportunity",
        1480,
        "🔄 *Renewal Opportunity Created*\\n*Account:* {customer_name} ({customer_email})\\n*Deal:* {deal_name} | Amount: ${deal_amount}\\n*Renewal Date:* {renewal_date} (90 days out)\\n*Health Score:* {health_score}% | *Risk:* {risk_level}\\n*Expansion:* {expansion_opportunity}\\n*Recommendation:* {renewal_recommendation}\\n*Renewal Deal ID:* {renewal_deal_id}\\n*Action:* Review value summary and schedule renewal conversation",
      ),

      // 8. Follow-up sequence scheduler delay
      n("8", "delayAction", "Schedule 14-day follow-up if no response", 1720, {
        delayDetails: { duration: 14, unit: "days" },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1658. RENEWAL OR CROSS-SELL NURTURE FOR EXISTING ACCOUNT (9 nodes)
  // ===================================================================

  "Renewal Cross-Sell Nurture for Existing Account": {
    summary:
      "Receives a renewal window webhook, aggregates account intelligence from HubSpot, uses AI to identify cross-sell opportunities, routes by segment via condition, sends a Week 1 value recap email, waits 14 days, sends a Week 3 feature discovery email, sends a Week 5 customer story email, and syncs all activity to HubSpot CRM.",
    nodes: [
      trigger("Webhook: Account enters renewal window", "webhook_app", {
        source: "hubspot",
        event: "deal.renewal_window",
        howItStarts:
          "Fires when an existing account enters the renewal evaluation window (typically 60-120 days before contract expiration), initiating a multi-touch nurture campaign combining renewal engagement with cross-sell opportunities",
        setup:
          "Configure HubSpot workflow trigger for renewal window entry. Set OpenAI API key for cross-sell analysis. Ensure email templates are configured for the three-touch sequence.",
        inputFields: [],
      }),

      // 2. Account intelligence aggregation HubSpot
      aiNode(
        "2",
        "AI extract account renewal context from webhook",
        280,
        "Extract renewal account data from this raw webhook payload. Find the contact email, contact name, company name, deal ID, deal amount, renewal date, contract start date, product tier, feature usage data, support ticket count, NPS score, and account owner email regardless of field naming. Return JSON: {contact_email, contact_name, company_name, deal_id, deal_amount, renewal_date, contract_start, product_tier, feature_usage, support_tickets, nps_score, owner_email}",
        "Raw webhook payload: {webhook_payload}",
        [
          { field: "contact_email", as: "contact_email", type: "string" },
          { field: "contact_name", as: "contact_name", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "deal_id", as: "deal_id", type: "string" },
          { field: "deal_amount", as: "deal_amount", type: "string" },
          { field: "renewal_date", as: "renewal_date", type: "string" },
          { field: "contract_start", as: "contract_start", type: "string" },
          { field: "product_tier", as: "product_tier", type: "string" },
          { field: "feature_usage", as: "feature_usage", type: "string" },
          { field: "support_tickets", as: "support_tickets", type: "string" },
          { field: "nps_score", as: "nps_score", type: "string" },
          { field: "owner_email", as: "owner_email", type: "string" },
        ],
      ),

      // 3. AI cross-sell opportunity identifier
      aiNode(
        "3",
        "AI identify cross-sell and upsell opportunities",
        520,
        "You are a revenue expansion strategist. Given the account context including product tier, feature usage, support history, and NPS, identify the best cross-sell and upsell opportunities. Consider underutilized features as upsell signals and adjacent products as cross-sell opportunities. Return JSON: {segment, cross_sell_product, cross_sell_value, upsell_feature, upsell_value, value_recap_points, feature_discovery_topics, customer_story_angle, expansion_potential, recommended_tier}",
        "Account: {company_name} | Tier: {product_tier} | Amount: ${deal_amount} | Features Used: {feature_usage} | Support Tickets: {support_tickets} | NPS: {nps_score} | Contract Duration: {contract_start} to {renewal_date}",
        [
          { field: "segment", as: "account_segment", type: "string" },
          {
            field: "cross_sell_product",
            as: "cross_sell_product",
            type: "string",
          },
          { field: "cross_sell_value", as: "cross_sell_value", type: "string" },
          { field: "upsell_feature", as: "upsell_feature", type: "string" },
          { field: "upsell_value", as: "upsell_value", type: "string" },
          { field: "value_recap_points", as: "value_recap", type: "string" },
          {
            field: "feature_discovery_topics",
            as: "feature_topics",
            type: "string",
          },
          { field: "customer_story_angle", as: "story_angle", type: "string" },
          {
            field: "expansion_potential",
            as: "expansion_potential",
            type: "string",
          },
          { field: "recommended_tier", as: "recommended_tier", type: "string" },
        ],
      ),

      // 4. Segment routing condition
      n(
        "4",
        "condition",
        "Route by account segment for content personalization",
        760,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "account_segment",
                operator: "equals",
                value: "high_value",
              },
            ],
            logicalOperator: "AND",
          },
        },
      ),

      // 5. Week 1 value recap email
      emailNode(
        "5",
        "Send Week 1 value recap and partnership highlights",
        1000,
        "{contact_email}",
        "A look back at what {company_name} achieved this year",
        "Hi {contact_name},\\n\\nAs your renewal date ({renewal_date}) approaches, we wanted to share a recap of the incredible value {company_name} has driven with our platform.\\n\\nYour Value Highlights:\\n{value_recap}\\n\\nCurrent Plan: {product_tier} at ${deal_amount}/year\\n\\nKey Stats:\\n- Feature utilization: {feature_usage}\\n- Support interactions: {support_tickets} tickets resolved\\n- Satisfaction: NPS {nps_score}\\n\\nWe are grateful for our partnership and excited about what is ahead. Stay tuned for some exciting feature discoveries in our next update.\\n\\nBest regards",
      ),

      // 6. Delay 14 days
      n(
        "6",
        "delayAction",
        "Wait 14 days before feature discovery email",
        1240,
        {
          delayDetails: { duration: 14, unit: "days" },
        },
      ),

      // 7. Week 3 feature discovery email
      emailNode(
        "7",
        "Send Week 3 feature discovery and cross-sell email",
        1480,
        "{contact_email}",
        "Unlock more value with {company_name}'s plan, {contact_name}",
        "Hi {contact_name},\\n\\nDid you know there are powerful features in your plan that could amplify your results even further?\\n\\nFeature Discovery:\\n{feature_topics}\\n\\nPlus, many customers like {company_name} have seen great results by adding:\\n- {cross_sell_product}: {cross_sell_value}\\n- {upsell_feature}: {upsell_value}\\n\\nExpansion Potential: {expansion_potential}\\n\\nRecommended Next Step: Consider upgrading to {recommended_tier} to unlock the full potential.\\n\\nWould you like a personalized walkthrough? Reply to schedule a 15-minute demo.\\n\\nBest regards",
      ),

      // 8. Week 5 customer story email (delay embedded)
      emailNode(
        "8",
        "Send Week 5 customer success story email",
        1720,
        "{contact_email}",
        "How companies like {company_name} are maximizing their investment",
        "Hi {contact_name},\\n\\nWe love sharing success stories from customers who have achieved remarkable results — just like {company_name}.\\n\\nCustomer Story Spotlight:\\n{story_angle}\\n\\nWhat they achieved mirrors your own journey:\\n{value_recap}\\n\\nAs your renewal approaches on {renewal_date}, we want to make sure you are getting every ounce of value from your investment.\\n\\nLet us connect to discuss your renewal and explore how {cross_sell_product} could take your results to the next level.\\n\\nBook a renewal conversation: simply reply to this email with your availability.\\n\\nBest regards",
      ),

      // 9. CRM opportunity activity sync HubSpot
      hubspotPostNode(
        "9",
        "Sync nurture sequence activity to CRM deal",
        1960,
        "crm/v3/objects/deals/{deal_id}",
        [
          {
            key: "properties",
            value:
              '{"notes_last_updated":"Renewal nurture sequence completed (3 touches). Value recap, feature discovery, and customer story emails sent. Cross-sell: {cross_sell_product} ({cross_sell_value}). Upsell: {upsell_feature}. Expansion potential: {expansion_potential}. Recommended tier: {recommended_tier}."}',
          },
        ],
        [
          { name: "id", customName: "synced_deal_id", type: "string" },
          {
            name: "properties.notes_last_updated",
            customName: "nurture_logged",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1659. REPEAT SITE VISIT NURTURE (8 nodes)
  // ===================================================================

  "Repeat Site Visit Nurture": {
    summary:
      "Receives a repeat visit detection webhook, scores visitor intent with runCode, looks up the CRM contact in HubSpot, uses AI to draft personalized outreach, branches by intent level via condition, sends a personalized email, alerts the SDR in real-time via Slack, and logs engagement to the CRM.",
    nodes: [
      trigger("Webhook: Repeat website visitor detected", "webhook_app", {
        source: "segment",
        event: "visitor.repeat_detected",
        howItStarts:
          "Fires when a known contact revisits the website multiple times within a short window (e.g., 3+ visits in 7 days), indicating active research and buying intent",
        setup:
          "Configure website analytics webhook for repeat visitor detection. Ensure visitor identification matches CRM contacts. Set up HubSpot API token for contact lookup. Configure Slack webhook for SDR alerts.",
        inputFields: [],
      }),

      // 2. Visitor intent scoring runCode
      n("2", "runCode", "Score visitor intent from repeat visit data", 280, {
        runCodeDetails: {
          code: `const payload = JSON.parse(variables.webhook_payload || '{}');
const data = payload.data || payload;
const visitor = data.visitor || data.user || data;
output.visitor_email = visitor.email || visitor.traits?.email || '';
output.visitor_name = visitor.name || visitor.traits?.name || '';
output.visit_count = parseInt(data.visit_count || data.sessions || '1');
output.pages_viewed = (data.pages || data.pages_viewed || []).join(', ') || '';
output.time_on_site = parseInt(data.total_time || data.session_duration || '0');
output.last_page = data.last_page || data.current_page || '';
output.referrer = data.referrer || data.utm_source || 'direct';
output.viewed_pricing = (output.pages_viewed.toLowerCase().includes('pricing')) ? 'yes' : 'no';
output.viewed_demo = (output.pages_viewed.toLowerCase().includes('demo') || output.pages_viewed.toLowerCase().includes('trial')) ? 'yes' : 'no';
const intentBase = Math.min(100, output.visit_count * 15 + output.time_on_site / 60);
const intentBoost = (output.viewed_pricing === 'yes' ? 20 : 0) + (output.viewed_demo === 'yes' ? 15 : 0);
output.intent_score = Math.min(100, Math.round(intentBase + intentBoost));
output.intent_level = output.intent_score >= 70 ? 'high' : output.intent_score >= 40 ? 'medium' : 'low';`,
          outputVariables: [
            { name: "visitor_email", type: "string" },
            { name: "visitor_name", type: "string" },
            { name: "visit_count", type: "number" },
            { name: "pages_viewed", type: "string" },
            { name: "time_on_site", type: "number" },
            { name: "last_page", type: "string" },
            { name: "referrer", type: "string" },
            { name: "viewed_pricing", type: "string" },
            { name: "viewed_demo", type: "string" },
            { name: "intent_score", type: "number" },
            { name: "intent_level", type: "string" },
          ],
        },
      }),

      // 3. CRM contact lookup HubSpot
      hubspotGetNode(
        "3",
        "Look up visitor in HubSpot CRM",
        520,
        "crm/v3/objects/contacts/{visitor_email}?properties=email,firstname,lastname,company,jobtitle,lifecyclestage,hs_lead_status,hubspot_owner_id,num_associated_deals",
        [
          { name: "id", customName: "contact_id", type: "string" },
          {
            name: "properties.firstname",
            customName: "first_name",
            type: "string",
          },
          {
            name: "properties.lastname",
            customName: "last_name",
            type: "string",
          },
          {
            name: "properties.company",
            customName: "company_name",
            type: "string",
          },
          {
            name: "properties.jobtitle",
            customName: "job_title",
            type: "string",
          },
          {
            name: "properties.lifecyclestage",
            customName: "lifecycle_stage",
            type: "string",
          },
          {
            name: "properties.hubspot_owner_id",
            customName: "contact_owner",
            type: "string",
          },
          {
            name: "properties.num_associated_deals",
            customName: "associated_deals",
            type: "string",
          },
        ],
      ),

      // 4. AI personalized outreach drafter
      aiNode(
        "4",
        "AI draft personalized outreach based on browsing behavior",
        760,
        "You are an SDR crafting hyper-personalized outreach. Based on the visitor browsing behavior (pages viewed, time on site, pricing page views, demo page views) and CRM context (company, title, lifecycle stage), write a brief, relevant outreach email. Reference specific pages they viewed without being creepy. Return JSON: {subject_line, email_body, conversation_starter, value_hook, cta}",
        "Visitor: {first_name} {last_name}, {job_title} at {company_name} | Visits: {visit_count} | Pages: {pages_viewed} | Last Page: {last_page} | Time: {time_on_site}s | Pricing Viewed: {viewed_pricing} | Demo Viewed: {viewed_demo} | Intent: {intent_score}/100 ({intent_level}) | CRM Stage: {lifecycle_stage} | Deals: {associated_deals}",
        [
          { field: "subject_line", as: "outreach_subject", type: "string" },
          { field: "email_body", as: "outreach_body", type: "string" },
          {
            field: "conversation_starter",
            as: "conversation_starter",
            type: "string",
          },
          { field: "value_hook", as: "value_hook", type: "string" },
          { field: "cta", as: "outreach_cta", type: "string" },
        ],
      ),

      // 5. Intent-based routing condition
      n(
        "5",
        "condition",
        "Check if visitor intent is high for immediate outreach",
        1000,
        {
          conditionDetails: {
            conditions: [
              { variable: "intent_level", operator: "equals", value: "high" },
            ],
            logicalOperator: "AND",
          },
        },
      ),

      // 6. Personalized email send (True path)
      emailNode(
        "6",
        "Send personalized outreach email to high-intent visitor",
        1240,
        "{visitor_email}",
        "{outreach_subject}",
        "{outreach_body}",
      ),

      // 7. SDR real-time alert Slack
      slackNode(
        "7",
        "Alert SDR of high-intent repeat visitor",
        1480,
        "🎯 *High-Intent Repeat Visitor Detected*\\n*Visitor:* {first_name} {last_name} ({visitor_email})\\n*Company:* {company_name} | *Title:* {job_title}\\n*Intent Score:* {intent_score}/100 ({intent_level})\\n*Visit Count:* {visit_count} | *Time on Site:* {time_on_site}s\\n*Pages:* {pages_viewed}\\n*Pricing Viewed:* {viewed_pricing} | *Demo Viewed:* {viewed_demo}\\n*CRM Stage:* {lifecycle_stage} | *Deals:* {associated_deals}\\n*Conversation Starter:* {conversation_starter}\\n*Value Hook:* {value_hook}",
      ),

      // 8. CRM engagement log HubSpot
      hubspotPostNode(
        "8",
        "Log repeat visit engagement to CRM contact",
        1720,
        "crm/v3/objects/contacts/{contact_id}",
        [
          {
            key: "properties",
            value:
              '{"notes_last_updated":"Repeat visit detected ({visit_count} visits). Intent: {intent_score}/100 ({intent_level}). Pages: {pages_viewed}. Pricing: {viewed_pricing}. Demo: {viewed_demo}. Outreach email sent.","hs_lead_status":"IN_PROGRESS"}',
          },
        ],
        [
          { name: "id", customName: "updated_contact_id", type: "string" },
          {
            name: "properties.hs_lead_status",
            customName: "updated_status",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "8", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1660. REQUEST-A-QUOTE NURTURE (8 nodes)
  // ===================================================================

  "Request-a-Quote Nurture": {
    summary:
      "Receives a quote request form submission via webhook, creates a CRM lead and deal in HubSpot, sends an immediate acknowledgment email, uses AI to match relevant content, sends Day 1 supporting materials email, waits 2 days, sends Day 3 social proof email, and monitors quote delivery status via condition.",
    nodes: [
      trigger("Webhook: Quote request form submitted", "webhook_form", {
        source: "website",
        event: "form.quote_request",
        howItStarts:
          "Fires when a prospect submits a request-a-quote form on the website, providing their contact details, product or service interest, quantity, and timeline requirements",
        setup:
          "Configure form webhook for quote request submissions. Set HubSpot API token for lead and deal creation. Prepare supporting materials and social proof content libraries.",
        inputFields: [],
      }),

      // 2. CRM lead deal creation HubSpot
      aiNode(
        "2",
        "AI extract quote request details from form submission",
        280,
        "Extract all quote request details from this raw form submission. Find the contact name, email, phone, company name, product or service requested, quantity or scope, budget range, timeline, special requirements, and how they found us. Return JSON: {contact_name, contact_email, contact_phone, company_name, product_requested, quantity, budget_range, timeline, special_requirements, lead_source}",
        "Raw form submission: {webhook_payload}. Extract all quote request fields regardless of naming format.",
        [
          { field: "contact_name", as: "contact_name", type: "string" },
          { field: "contact_email", as: "contact_email", type: "string" },
          { field: "contact_phone", as: "contact_phone", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          {
            field: "product_requested",
            as: "product_requested",
            type: "string",
          },
          { field: "quantity", as: "quantity", type: "string" },
          { field: "budget_range", as: "budget_range", type: "string" },
          { field: "timeline", as: "timeline", type: "string" },
          {
            field: "special_requirements",
            as: "special_requirements",
            type: "string",
          },
          { field: "lead_source", as: "lead_source", type: "string" },
        ],
      ),

      // 3. Immediate acknowledgment email
      hubspotPostNode(
        "3",
        "Create CRM lead and deal for quote request",
        520,
        "crm/v3/objects/deals",
        [
          {
            key: "properties",
            value:
              '{"dealname":"Quote Request: {company_name} — {product_requested}","dealstage":"appointmentscheduled","pipeline":"default","description":"Quote request from {contact_name} at {company_name}. Product: {product_requested}. Quantity: {quantity}. Budget: {budget_range}. Timeline: {timeline}. Requirements: {special_requirements}. Source: {lead_source}."}',
          },
        ],
        [
          { name: "id", customName: "deal_id", type: "string" },
          {
            name: "properties.dealname",
            customName: "deal_name",
            type: "string",
          },
        ],
      ),

      // 4. Acknowledgment email
      emailNode(
        "4",
        "Send immediate quote request acknowledgment",
        760,
        "{contact_email}",
        "We received your quote request, {contact_name} — Here is what happens next",
        "Hi {contact_name},\\n\\nThank you for requesting a quote from us! We have received your inquiry and our team is already preparing your personalized quote.\\n\\nYour Request Summary:\\n- Product/Service: {product_requested}\\n- Quantity/Scope: {quantity}\\n- Budget Range: {budget_range}\\n- Timeline: {timeline}\\n- Special Requirements: {special_requirements}\\n\\nWhat to Expect:\\n1. A dedicated specialist will review your requirements within 4 business hours\\n2. Your personalized quote will be prepared and sent within 1 business day\\n3. We will include relevant case studies and specifications\\n\\nIn the meantime, if you have any questions or additional details to share, simply reply to this email.\\n\\nBest regards",
      ),

      // 5. AI content matcher
      aiNode(
        "5",
        "AI match relevant supporting content for quote request",
        1000,
        "You are a content strategist. Based on the product requested, company details, and special requirements, select the most relevant supporting materials to send. Choose a case study topic, a product comparison angle, a ROI statistic, and a customer testimonial focus. Return JSON: {case_study_topic, comparison_angle, roi_statistic, testimonial_focus, social_proof_company, social_proof_result}",
        "Product: {product_requested} | Company: {company_name} | Budget: {budget_range} | Timeline: {timeline} | Requirements: {special_requirements}",
        [
          { field: "case_study_topic", as: "case_study_topic", type: "string" },
          { field: "comparison_angle", as: "comparison_angle", type: "string" },
          { field: "roi_statistic", as: "roi_statistic", type: "string" },
          {
            field: "testimonial_focus",
            as: "testimonial_focus",
            type: "string",
          },
          {
            field: "social_proof_company",
            as: "social_proof_company",
            type: "string",
          },
          {
            field: "social_proof_result",
            as: "social_proof_result",
            type: "string",
          },
        ],
      ),

      // 6. Day 1 supporting materials email
      emailNode(
        "6",
        "Send Day 1 supporting materials and case study",
        1240,
        "{contact_email}",
        "While your quote is being prepared — Resources for {product_requested}",
        "Hi {contact_name},\\n\\nWhile our team finalizes your personalized quote for {product_requested}, I wanted to share some resources that other customers in similar situations have found valuable.\\n\\nRelevant Case Study: {case_study_topic}\\nThis case study shows how a company similar to {company_name} achieved remarkable results with our solution.\\n\\nKey ROI Finding: {roi_statistic}\\n\\nProduct Comparison: {comparison_angle}\\n\\nThese resources should help inform your evaluation as you review the quote we are preparing.\\n\\nYour quote is on track to be delivered within our standard timeline.\\n\\nBest regards",
      ),

      // 7. Delay 2 days
      n("7", "delayAction", "Wait 2 days before social proof email", 1480, {
        delayDetails: { duration: 2, unit: "days" },
      }),

      // 8. Day 3 social proof email + condition
      emailNode(
        "8",
        "Send Day 3 social proof and testimonial email",
        1720,
        "{contact_email}",
        "See how {social_proof_company} achieved {social_proof_result}",
        "Hi {contact_name},\\n\\nI wanted to share a quick success story that is particularly relevant to your {product_requested} inquiry.\\n\\n{social_proof_company} — {social_proof_result}:\\n\\nTestimonial Focus: {testimonial_focus}\\n\\nLike {company_name}, they were evaluating {comparison_angle} and chose our solution based on the value we deliver.\\n\\nHave you had a chance to review the supporting materials we sent? If you have any questions about {product_requested} or want to discuss your specific requirements ({special_requirements}), I am here to help.\\n\\nLooking forward to sending your personalized quote soon!\\n\\nBest regards",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1661. RESEARCH REPORT RELEASE NURTURE (9 nodes)
  // ===================================================================

  "Research Report Release Nurture": {
    summary:
      "Receives a webhook when a research report is published, segments the audience in HubSpot, uses AI to extract key findings, sends a Day 0 launch email, tracks downloads via condition, sends a Day 3 key finding deep dive, waits 4 more days, sends a Day 7 finding plus webinar invite, and hands off engaged leads to HubSpot for sales follow-up.",
    nodes: [
      trigger("Webhook: Research report published", "webhook_app", {
        source: "cms",
        event: "content.report_published",
        howItStarts:
          "Fires when a new research report, whitepaper, or industry analysis is published on the CMS, initiating a multi-touch nurture campaign to drive downloads and engagement",
        setup:
          "Configure CMS webhook for content publication events filtered to report type. Set HubSpot API token for audience segmentation. Configure download tracking integration.",
        inputFields: [],
      }),

      // 2. Audience segmentation engine HubSpot
      aiNode(
        "2",
        "AI extract report publication details from webhook",
        280,
        "Extract the research report publication details from the raw webhook payload. Find the report title, report URL, report topic, author name, publication date, executive summary, target audience, and content tags. Return JSON: {report_title, report_url, report_topic, author_name, pub_date, executive_summary, target_audience, content_tags, report_id}",
        "Raw webhook payload: {webhook_payload}. Extract all report publication details regardless of CMS field naming conventions.",
        [
          { field: "report_title", as: "report_title", type: "string" },
          { field: "report_url", as: "report_url", type: "string" },
          { field: "report_topic", as: "report_topic", type: "string" },
          { field: "author_name", as: "author_name", type: "string" },
          { field: "pub_date", as: "pub_date", type: "string" },
          { field: "executive_summary", as: "exec_summary", type: "string" },
          { field: "target_audience", as: "target_audience", type: "string" },
          { field: "content_tags", as: "content_tags", type: "string" },
          { field: "report_id", as: "report_id", type: "string" },
        ],
      ),

      // 3. AI key findings extractor
      aiNode(
        "3",
        "AI extract and summarize key findings from report",
        520,
        "You are a research analyst who creates compelling content summaries. Given the report details and executive summary, extract the top 3 key findings, a surprising statistic, an actionable insight, a quotable headline, and a webinar topic idea related to the findings. Each finding should be a concise 1-2 sentence summary. Return JSON: {finding_1, finding_2, finding_3, surprising_stat, actionable_insight, quotable_headline, webinar_topic, subscriber_list_id}",
        "Report: {report_title} | Topic: {report_topic} | Audience: {target_audience} | Tags: {content_tags} | Summary: {exec_summary}",
        [
          { field: "finding_1", as: "finding_1", type: "string" },
          { field: "finding_2", as: "finding_2", type: "string" },
          { field: "finding_3", as: "finding_3", type: "string" },
          { field: "surprising_stat", as: "surprising_stat", type: "string" },
          {
            field: "actionable_insight",
            as: "actionable_insight",
            type: "string",
          },
          {
            field: "quotable_headline",
            as: "quotable_headline",
            type: "string",
          },
          { field: "webinar_topic", as: "webinar_topic", type: "string" },
          {
            field: "subscriber_list_id",
            as: "subscriber_list_id",
            type: "string",
          },
        ],
      ),

      // 4. Day 0 launch email
      emailNode(
        "4",
        "Send Day 0 report launch announcement email",
        760,
        "{target_audience}",
        "New Research: {report_title}",
        "Hi,\\n\\n{quotable_headline}\\n\\nWe are excited to share our latest research report: {report_title}\\n\\nBy {author_name} | Published {pub_date}\\n\\n{exec_summary}\\n\\nKey Finding Preview:\\n{finding_1}\\n\\nSurprising Stat: {surprising_stat}\\n\\nDownload the full report here: {report_url}\\n\\nOver the coming days, we will share deeper dives into each key finding. Stay tuned!\\n\\nBest regards",
      ),

      // 5. Download tracking condition
      n(
        "5",
        "condition",
        "Check if report has subscriber list for targeted follow-up",
        1000,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "subscriber_list_id",
                operator: "not_empty",
                value: "",
              },
            ],
            logicalOperator: "AND",
          },
        },
      ),

      // 6. Day 3 key finding deep dive email (True path)
      emailNode(
        "6",
        "Send Day 3 key finding #1 deep dive email",
        1240,
        "{target_audience}",
        "Deep Dive: {finding_1}",
        "Hi,\\n\\nIn our new report '{report_title}', one finding stood out more than any other:\\n\\n{finding_1}\\n\\nWhy this matters:\\n{actionable_insight}\\n\\nThe data reveals that {surprising_stat} — a trend that has significant implications for {report_topic} strategy.\\n\\nKey Takeaway for Your Team:\\nThis finding suggests concrete steps you can take today to stay ahead. Here is what leading organizations are already doing differently.\\n\\nRead the full analysis: {report_url}\\n\\nStay tuned — in our next email, we will dive into Finding #2: {finding_2}\\n\\nBest regards",
      ),

      // 7. Delay 4 days
      n(
        "7",
        "delayAction",
        "Wait 4 days before key finding 2 and webinar invite",
        1480,
        {
          delayDetails: { duration: 4, unit: "days" },
        },
      ),

      // 8. Day 7 key finding 2 + webinar invite email
      emailNode(
        "8",
        "Send Day 7 key finding #2 and webinar invitation",
        1720,
        "{target_audience}",
        "Finding #2 from our research + Live webinar invitation",
        "Hi,\\n\\nHere is the second key finding from '{report_title}':\\n\\n{finding_2}\\n\\nAnd there is more — {finding_3}\\n\\nJoin Us Live — {webinar_topic}:\\nWe are hosting a live webinar to discuss these findings in depth with {author_name} and industry experts.\\n\\nWhat we will cover:\\n- Deep analysis of all three key findings\\n- Real-world applications and case studies\\n- Live Q&A with the research team\\n- Actionable frameworks you can implement immediately\\n\\nDo not miss this opportunity to go beyond the report and get expert perspectives.\\n\\nHave not downloaded the report yet? Get it here: {report_url}\\n\\nBest regards",
      ),

      // 9. Engagement-based handoff HubSpot
      hubspotPostNode(
        "9",
        "Hand off engaged report leads to sales in HubSpot",
        1960,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"notes_last_updated":"Research report nurture completed: {report_title}. 3-touch sequence delivered (launch, deep dive, webinar invite). Report topic: {report_topic}. Key findings: {finding_1}; {finding_2}; {finding_3}. Webinar topic: {webinar_topic}.","hs_lead_status":"OPEN"}',
          },
        ],
        [
          { name: "id", customName: "handoff_contact_id", type: "string" },
          {
            name: "properties.hs_lead_status",
            customName: "handoff_status",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "9", "output_2"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1662. SDR HANDOFF TO AE AUTOMATION (9 nodes)
  // ===================================================================

  "SDR Handoff to AE Automation": {
    summary:
      "Receives a lead-qualified webhook, builds a lead intelligence package from HubSpot, uses AI to generate a handoff brief, routes to the right AE via condition, transfers CRM ownership in HubSpot, notifies the AE with briefing via Slack, sends a warm introduction email to the prospect, creates a calendar invite via Google Calendar API, and monitors handoff SLA with a 4-hour delay.",
    nodes: [
      trigger("Webhook: Lead qualified by SDR", "webhook_app", {
        source: "hubspot",
        event: "contact.qualified",
        howItStarts:
          "Fires when an SDR marks a lead as qualified (SQL) in the CRM, triggering the automated handoff to an Account Executive including intelligence briefing and prospect introduction",
        setup:
          "Configure HubSpot webhook for contact lifecycle stage change to SQL. Set up Slack webhook for AE notifications. Configure Google Calendar API for meeting scheduling. Ensure CRM ownership transfer permissions.",
        inputFields: [],
      }),

      // 2. Lead intelligence package builder HubSpot
      aiNode(
        "2",
        "AI extract qualified lead details from webhook",
        280,
        "Extract the qualified lead details from the raw webhook payload. Find the contact ID, contact email, contact name, company name, phone, job title, lead source, qualification notes, deal value estimate, SDR name, SDR email, industry, company size, and any meeting notes or conversation summary. Return JSON: {contact_id, contact_email, contact_name, company_name, phone, job_title, lead_source, qualification_notes, deal_value, sdr_name, sdr_email, industry, company_size, conversation_summary}",
        "Raw webhook payload: {webhook_payload}. Extract all qualified lead and SDR handoff data regardless of field naming.",
        [
          { field: "contact_id", as: "contact_id", type: "string" },
          { field: "contact_email", as: "contact_email", type: "string" },
          { field: "contact_name", as: "contact_name", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "phone", as: "contact_phone", type: "string" },
          { field: "job_title", as: "job_title", type: "string" },
          { field: "lead_source", as: "lead_source", type: "string" },
          { field: "qualification_notes", as: "qual_notes", type: "string" },
          { field: "deal_value", as: "deal_value", type: "string" },
          { field: "sdr_name", as: "sdr_name", type: "string" },
          { field: "sdr_email", as: "sdr_email", type: "string" },
          { field: "industry", as: "industry", type: "string" },
          { field: "company_size", as: "company_size", type: "string" },
          {
            field: "conversation_summary",
            as: "conversation_summary",
            type: "string",
          },
        ],
      ),

      // 3. AI handoff brief generator
      aiNode(
        "3",
        "AI generate comprehensive AE handoff brief",
        520,
        "You are a sales operations specialist creating AE handoff briefs. Using the lead intelligence, create a comprehensive brief that helps the AE pick up the conversation seamlessly. Include the lead background, pain points identified, decision criteria, budget signals, timeline, competitive landscape, recommended talk track, and potential objections. Determine the best AE assignment based on industry and deal size (enterprise AE for >$50K, mid-market AE for $10K-$50K, SMB AE for <$10K). Return JSON: {ae_tier, ae_assignment_email, lead_background, pain_points, decision_criteria, budget_signal, decision_timeline, competitive_intel, recommended_talk_track, potential_objections, meeting_agenda_suggestion}",
        "Lead: {contact_name}, {job_title} at {company_name} ({industry}, {company_size} employees) | Source: {lead_source} | Value: ${deal_value} | SDR: {sdr_name} | Qualification Notes: {qual_notes} | Conversation: {conversation_summary}",
        [
          { field: "ae_tier", as: "ae_tier", type: "string" },
          { field: "ae_assignment_email", as: "ae_email", type: "string" },
          { field: "lead_background", as: "lead_background", type: "string" },
          { field: "pain_points", as: "pain_points", type: "string" },
          {
            field: "decision_criteria",
            as: "decision_criteria",
            type: "string",
          },
          { field: "budget_signal", as: "budget_signal", type: "string" },
          {
            field: "decision_timeline",
            as: "decision_timeline",
            type: "string",
          },
          {
            field: "competitive_intel",
            as: "competitive_intel",
            type: "string",
          },
          { field: "recommended_talk_track", as: "talk_track", type: "string" },
          { field: "potential_objections", as: "objections", type: "string" },
          {
            field: "meeting_agenda_suggestion",
            as: "meeting_agenda",
            type: "string",
          },
        ],
      ),

      // 4. AE assignment engine condition
      n("4", "condition", "Route to enterprise AE for large deals", 760, {
        conditionDetails: {
          conditions: [
            { variable: "ae_tier", operator: "equals", value: "enterprise" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 5. CRM ownership transfer HubSpot
      hubspotPostNode(
        "5",
        "Transfer CRM contact ownership to assigned AE",
        1000,
        "crm/v3/objects/contacts/{contact_id}",
        [
          {
            key: "properties",
            value:
              '{"hubspot_owner_id":"{ae_email}","lifecyclestage":"salesqualifiedlead","hs_lead_status":"CONNECTED","notes_last_updated":"SDR-to-AE handoff. SDR: {sdr_name}. AE Tier: {ae_tier}. Deal Value: ${deal_value}. Pain Points: {pain_points}. Decision Criteria: {decision_criteria}. Timeline: {decision_timeline}."}',
          },
        ],
        [
          { name: "id", customName: "transferred_contact_id", type: "string" },
          {
            name: "properties.hubspot_owner_id",
            customName: "new_owner",
            type: "string",
          },
        ],
      ),

      // 6. AE notification with briefing Slack
      slackNode(
        "6",
        "Notify AE with comprehensive handoff briefing",
        1240,
        "🤝 *New SDR-to-AE Handoff*\\n*Lead:* {contact_name}, {job_title} at {company_name}\\n*Email:* {contact_email} | *Phone:* {contact_phone}\\n*Industry:* {industry} | *Size:* {company_size} | *Value:* ${deal_value}\\n*AE Tier:* {ae_tier}\\n*SDR:* {sdr_name}\\n\\n*Background:* {lead_background}\\n*Pain Points:* {pain_points}\\n*Decision Criteria:* {decision_criteria}\\n*Budget Signal:* {budget_signal}\\n*Timeline:* {decision_timeline}\\n*Competition:* {competitive_intel}\\n*Talk Track:* {talk_track}\\n*Objections:* {objections}\\n*Meeting Agenda:* {meeting_agenda}",
      ),

      // 7. Warm introduction email
      emailNode(
        "7",
        "Send warm introduction email from AE to prospect",
        1480,
        "{contact_email}",
        "{contact_name}, meet your dedicated Account Executive",
        "Hi {contact_name},\\n\\nI am excited to connect with you! {sdr_name} shared the great conversation you had about {company_name}'s needs, and I will be your dedicated point of contact going forward.\\n\\nA bit about me — I specialize in helping {industry} companies like {company_name} solve exactly the challenges you described. I have reviewed your requirements and I am confident we can deliver significant value.\\n\\nBased on what {sdr_name} shared, I would love to schedule a deeper conversation to:\\n{meeting_agenda}\\n\\nWould any of the following times work for a 30-minute call? I will send a calendar invite once we confirm a time.\\n\\nLooking forward to our conversation!\\n\\nBest regards",
      ),

      // 8. Prospect calendar invite Google Calendar API
      n("8", "hitApi", "Create calendar hold for prospect meeting", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://www.googleapis.com/calendar/v3/calendars/primary/events",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "summary",
              value: "Intro Call: {contact_name} ({company_name})",
            },
            {
              key: "description",
              value:
                "Handoff from {sdr_name}. Agenda: {meeting_agenda}. Pain Points: {pain_points}. Value: ${deal_value}.",
            },
            {
              key: "attendees",
              value: '[{"email":"{ae_email}"},{"email":"{contact_email}"}]',
            },
          ],
          variables: [
            { name: "id", customName: "calendar_event_id", type: "string" },
            { name: "htmlLink", customName: "calendar_link", type: "string" },
            { name: "status", customName: "event_status", type: "string" },
          ],
        },
      }),

      // 9. Handoff SLA monitor delay 4hrs
      n(
        "9",
        "delayAction",
        "Monitor handoff SLA — 4 hour follow-up timer",
        1960,
        {
          delayDetails: { duration: 4, unit: "hours" },
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1663. SDR NO FOLLOW-UP ESCALATION (7 nodes)
  // ===================================================================

  "SDR No Follow-Up Escalation": {
    summary:
      "Runs on a 2-hour interval schedule, calculates SLA breach times with runCode, verifies activity against HubSpot records, checks SLA breach status via condition, sends an SDR reminder via Slack, escalates to the manager via Slack and email, and flags the audit log in HubSpot CRM.",
    nodes: [
      trigger("Schedule: SLA compliance checker every 2 hours", "schedule", {
        schedule: "Every 2 hours during business hours (8 AM - 6 PM)",
        howItStarts:
          "Runs every 2 hours during business hours to scan for leads that have not received follow-up within the defined SLA window, escalating to SDR and then to management as needed",
        setup:
          "Configure 2-hour interval schedule during business hours. Ensure HubSpot API token has contact and activity read/write access. Configure Slack webhooks for SDR and manager channels.",
        inputFields: [],
      }),

      // 2. SLA calculation engine runCode
      n("2", "runCode", "Calculate SLA breach status and time elapsed", 280, {
        runCodeDetails: {
          code: `const now = new Date();
const slaThresholdHours = 4;
const escalationThresholdHours = 8;
output.current_time = now.toISOString();
output.sla_threshold_ms = slaThresholdHours * 60 * 60 * 1000;
output.escalation_threshold_ms = escalationThresholdHours * 60 * 60 * 1000;
output.business_hours_start = 8;
output.business_hours_end = 18;
output.is_business_hours = now.getHours() >= 8 && now.getHours() < 18 ? 'yes' : 'no';
output.check_from_date = new Date(now.getTime() - escalationThresholdHours * 60 * 60 * 1000).toISOString();
output.sla_window = slaThresholdHours + ' hours';
output.escalation_window = escalationThresholdHours + ' hours';`,
          outputVariables: [
            { name: "current_time", type: "string" },
            { name: "sla_threshold_ms", type: "number" },
            { name: "escalation_threshold_ms", type: "number" },
            { name: "is_business_hours", type: "string" },
            { name: "check_from_date", type: "string" },
            { name: "sla_window", type: "string" },
            { name: "escalation_window", type: "string" },
          ],
        },
      }),

      // 3. Activity verification HubSpot
      hubspotGetNode(
        "3",
        "Check for leads with no recent activity in HubSpot",
        520,
        "crm/v3/objects/contacts/search",
        [
          { name: "total", customName: "breached_lead_count", type: "number" },
          {
            name: "results[0].id",
            customName: "first_lead_id",
            type: "string",
          },
          {
            name: "results[0].properties.email",
            customName: "first_lead_email",
            type: "string",
          },
          {
            name: "results[0].properties.firstname",
            customName: "first_lead_name",
            type: "string",
          },
          {
            name: "results[0].properties.company",
            customName: "first_lead_company",
            type: "string",
          },
          {
            name: "results[0].properties.hubspot_owner_id",
            customName: "assigned_sdr_id",
            type: "string",
          },
          {
            name: "results[0].properties.notes_last_contacted",
            customName: "last_contacted",
            type: "string",
          },
          {
            name: "results[0].properties.hs_lead_status",
            customName: "lead_status",
            type: "string",
          },
        ],
      ),

      // 4. SLA breach condition
      n("4", "condition", "Check if any leads have breached SLA", 760, {
        conditionDetails: {
          conditions: [
            {
              variable: "breached_lead_count",
              operator: "greater_than",
              value: "0",
            },
          ],
          logicalOperator: "AND",
        },
      }),

      // 5. SDR reminder notification Slack (True path)
      slackNode(
        "5",
        "Send SDR reminder for SLA-breached leads",
        1000,
        "⏰ *SLA Reminder — Leads Awaiting Follow-Up*\\n*Breached Leads:* {breached_lead_count} lead(s) past {sla_window} SLA\\n*First Lead:* {first_lead_name} at {first_lead_company} ({first_lead_email})\\n*Last Contacted:* {last_contacted}\\n*Lead Status:* {lead_status}\\n*SDR:* {assigned_sdr_id}\\n*Action Required:* Follow up immediately to maintain SLA compliance\\n*Escalation Warning:* Manager will be notified if not actioned within {escalation_window}",
      ),

      // 6. Manager escalation alert Slack + email
      slackNode(
        "6",
        "Escalate SLA breach to sales manager",
        1240,
        "🚨 *SLA Breach Escalation — Manager Action Required*\\n*Total Breached:* {breached_lead_count} leads past SLA\\n*Longest Breach:* {first_lead_name} at {first_lead_company}\\n*Last Contact:* {last_contacted}\\n*Assigned SDR:* {assigned_sdr_id}\\n*SLA Window:* {sla_window} | *Escalation Window:* {escalation_window}\\n*Current Time:* {current_time}\\n*Business Hours:* {is_business_hours}\\n*Action:* Investigate SDR follow-up gaps and reassign if necessary",
      ),

      // 7. CRM flag audit log HubSpot
      hubspotPostNode(
        "7",
        "Flag SLA breach in CRM audit log",
        1480,
        "crm/v3/objects/contacts/{first_lead_id}",
        [
          {
            key: "properties",
            value:
              '{"notes_last_updated":"SLA BREACH: No follow-up within {sla_window}. Last contact: {last_contacted}. SDR: {assigned_sdr_id}. Escalated to manager at {current_time}. Breached leads in batch: {breached_lead_count}.","hs_lead_status":"ATTEMPTED_TO_CONTACT"}',
          },
        ],
        [
          { name: "id", customName: "flagged_contact_id", type: "string" },
          {
            name: "properties.hs_lead_status",
            customName: "updated_lead_status",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1664. SMS LEAD QUALIFICATION FLOW (9 nodes)
  // ===================================================================

  "SMS Lead Qualification Flow": {
    summary:
      "Receives a lead-with-phone webhook, validates the phone number via Twilio API, sends an initial SMS outreach, uses AI to parse the response, checks conversation qualification status via condition, scores the lead with runCode, routes qualified leads via condition, updates the CRM record in HubSpot, and alerts the SDR for hot leads via Slack.",
    nodes: [
      trigger("Webhook: New lead with phone number captured", "webhook_form", {
        source: "website",
        event: "form.lead_with_phone",
        howItStarts:
          "Fires when a lead form submission includes a phone number, initiating an SMS-first qualification flow to quickly assess interest and readiness",
        setup:
          "Configure form webhook for lead submissions with phone numbers. Set Twilio API credentials (Account SID, Auth Token, Messaging Service SID). Ensure HubSpot API token has contact write access. Configure Slack webhook for SDR alerts.",
        inputFields: [],
      }),

      // 2. Phone number validation Twilio API
      aiNode(
        "2",
        "AI extract lead details with phone from form submission",
        280,
        "Extract lead details from the raw form submission. Find the name, email, phone number, company, job title, interest area, and how they found us. Clean and format the phone number to E.164 format. Return JSON: {lead_name, lead_email, lead_phone, lead_phone_e164, company_name, job_title, interest_area, lead_source, is_valid_phone}",
        "Raw form submission: {webhook_payload}. Extract all lead fields and format the phone number to E.164 regardless of input format.",
        [
          { field: "lead_name", as: "lead_name", type: "string" },
          { field: "lead_email", as: "lead_email", type: "string" },
          { field: "lead_phone", as: "lead_phone", type: "string" },
          { field: "lead_phone_e164", as: "phone_e164", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "job_title", as: "job_title", type: "string" },
          { field: "interest_area", as: "interest_area", type: "string" },
          { field: "lead_source", as: "lead_source", type: "string" },
          { field: "is_valid_phone", as: "is_valid_phone", type: "string" },
        ],
      ),

      // 3. Phone validation via Twilio Lookup
      n("3", "hitApi", "Validate phone number via Twilio Lookup API", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://lookups.twilio.com/v2/PhoneNumbers/{phone_e164}?Fields=line_type_intelligence",
          headers: [
            { key: "Authorization", value: "Basic {twilio_auth_base64}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "valid", customName: "phone_valid", type: "boolean" },
            {
              name: "calling_country_code",
              customName: "country_code",
              type: "string",
            },
            {
              name: "phone_number",
              customName: "validated_phone",
              type: "string",
            },
            {
              name: "line_type_intelligence.type",
              customName: "phone_type",
              type: "string",
            },
          ],
        },
      }),

      // 4. Initial SMS outreach Twilio
      smsNode(
        "4",
        "Send initial SMS qualification outreach",
        760,
        "{validated_phone}",
        "Hi {lead_name}! Thanks for your interest in {interest_area}. This is a quick text to connect. Are you currently evaluating solutions for {company_name}? Reply YES to chat, or CALL to schedule a call. — Your Sales Team",
      ),

      // 5. AI response listener parser
      aiNode(
        "5",
        "AI parse SMS response for qualification signals",
        1000,
        "You are an SMS conversation analyst. Parse the prospect SMS response to determine their qualification level. Look for buying signals, timeline indicators, budget mentions, and engagement level. Classify as: qualified (clear interest + timeline), interested (positive response but no timeline), not_ready (browsing), or unresponsive (no meaningful reply). Return JSON: {response_text, qualification_status, buying_signals, timeline_indicator, budget_mention, engagement_level, next_action, sentiment}",
        "Lead: {lead_name} at {company_name} ({job_title}) | Interest: {interest_area} | SMS Response to analyze: {sms_response_text}",
        [
          { field: "qualification_status", as: "qual_status", type: "string" },
          { field: "buying_signals", as: "buying_signals", type: "string" },
          {
            field: "timeline_indicator",
            as: "timeline_indicator",
            type: "string",
          },
          { field: "budget_mention", as: "budget_mention", type: "string" },
          { field: "engagement_level", as: "engagement_level", type: "string" },
          { field: "next_action", as: "next_action", type: "string" },
          { field: "sentiment", as: "response_sentiment", type: "string" },
        ],
      ),

      // 6. Conversational qualification loop condition
      n(
        "6",
        "condition",
        "Check if lead response shows qualification signals",
        1240,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "qual_status",
                operator: "equals",
                value: "qualified",
              },
            ],
            logicalOperator: "AND",
          },
        },
      ),

      // 7. Lead scoring engine runCode (True path — qualified)
      n("7", "runCode", "Calculate lead qualification score", 1480, {
        runCodeDetails: {
          code: `let score = 0;
score += variables.qual_status === 'qualified' ? 40 : variables.qual_status === 'interested' ? 20 : 5;
score += variables.engagement_level === 'high' ? 25 : variables.engagement_level === 'medium' ? 15 : 5;
score += variables.timeline_indicator !== 'none' ? 15 : 0;
score += variables.budget_mention !== 'none' ? 15 : 0;
score += variables.response_sentiment === 'positive' ? 10 : variables.response_sentiment === 'neutral' ? 5 : 0;
output.lead_score = Math.min(100, score);
output.lead_grade = score >= 70 ? 'A' : score >= 50 ? 'B' : score >= 30 ? 'C' : 'D';
output.is_hot = score >= 70 ? 'yes' : 'no';
output.priority = score >= 70 ? 'high' : score >= 50 ? 'medium' : 'low';
output.score_breakdown = 'Qual: ' + variables.qual_status + ', Engagement: ' + variables.engagement_level + ', Timeline: ' + variables.timeline_indicator + ', Budget: ' + variables.budget_mention;`,
          outputVariables: [
            { name: "lead_score", type: "number" },
            { name: "lead_grade", type: "string" },
            { name: "is_hot", type: "string" },
            { name: "priority", type: "string" },
            { name: "score_breakdown", type: "string" },
          ],
        },
      }),

      // 8. CRM record update HubSpot
      hubspotPostNode(
        "8",
        "Update CRM record with SMS qualification data",
        1720,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{lead_email}","phone":"{validated_phone}","company":"{company_name}","jobtitle":"{job_title}","hs_lead_status":"{qual_status}","notes_last_updated":"SMS qualification completed. Score: {lead_score}/100 ({lead_grade}). Status: {qual_status}. Buying signals: {buying_signals}. Timeline: {timeline_indicator}. Sentiment: {response_sentiment}. Next action: {next_action}. Breakdown: {score_breakdown}"}',
          },
        ],
        [
          { name: "id", customName: "updated_contact_id", type: "string" },
          {
            name: "properties.hs_lead_status",
            customName: "crm_lead_status",
            type: "string",
          },
        ],
      ),

      // 9. SDR alert for hot leads Slack
      slackNode(
        "9",
        "Alert SDR for hot SMS-qualified leads",
        1960,
        "🔥 *Hot SMS-Qualified Lead*\\n*Lead:* {lead_name} ({lead_email})\\n*Phone:* {validated_phone} ({phone_type})\\n*Company:* {company_name} | *Title:* {job_title}\\n*Interest:* {interest_area}\\n*Score:* {lead_score}/100 ({lead_grade}) — Priority: {priority}\\n*Qualification:* {qual_status}\\n*Buying Signals:* {buying_signals}\\n*Timeline:* {timeline_indicator}\\n*Budget:* {budget_mention}\\n*Sentiment:* {response_sentiment}\\n*Next Action:* {next_action}\\n*Action:* Call immediately — hot lead via SMS qualification",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1665. SALES CALL SENTIMENT ANALYZER (8 nodes)
  // ===================================================================

  "Sales Call Sentiment Analyzer": {
    summary:
      "Receives a call recording ready webhook, retrieves the transcript from Gong API, uses AI for sentiment analysis, uses AI to extract key moments, assesses deal risk with runCode, generates a sentiment report with runCode, updates the CRM deal in HubSpot, and alerts the sales manager for coaching via Slack.",
    nodes: [
      trigger("Webhook: Sales call recording ready", "webhook_app", {
        source: "gong",
        event: "call.recording_ready",
        howItStarts:
          "Fires when a sales call recording is processed and ready for analysis in Gong, triggering automated sentiment analysis and coaching alerts",
        setup:
          "Configure Gong webhook for call.recording_ready events. Set Gong API credentials for transcript retrieval. Ensure HubSpot API token has deal write access. Configure Slack webhook for coaching channel.",
        inputFields: [],
      }),

      // 2. Transcript retrieval Gong API
      n("2", "hitApi", "Retrieve call transcript from Gong API", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.gong.io/v2/calls/transcript",
          headers: [
            { key: "Authorization", value: "Bearer {gong_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "filter", value: '{"callIds":["{call_id}"]}' }],
          variables: [
            {
              name: "callTranscripts[0].transcript",
              customName: "full_transcript",
              type: "string",
            },
            {
              name: "callTranscripts[0].callId",
              customName: "gong_call_id",
              type: "string",
            },
          ],
        },
      }),

      // 3. AI sentiment analysis
      aiNode(
        "3",
        "AI analyze call sentiment and emotional dynamics",
        520,
        "You are a sales call sentiment analyst. Analyze the full call transcript and assess: overall sentiment (positive, neutral, negative, mixed), prospect sentiment trajectory (improving, declining, stable), rep performance sentiment (confident, uncertain, aggressive, consultative), key emotional moments (objections, excitement, frustration, agreement), talk-to-listen ratio estimate, and engagement level. Return JSON: {overall_sentiment, prospect_trajectory, rep_style, emotional_moments, talk_listen_ratio, engagement_score, rapport_quality, closing_signal_strength, prospect_name, prospect_email, deal_id, rep_name}",
        "Full call transcript: {full_transcript}. Analyze sentiment patterns throughout the conversation, identifying shifts and key emotional turning points.",
        [
          {
            field: "overall_sentiment",
            as: "overall_sentiment",
            type: "string",
          },
          {
            field: "prospect_trajectory",
            as: "prospect_trajectory",
            type: "string",
          },
          { field: "rep_style", as: "rep_style", type: "string" },
          {
            field: "emotional_moments",
            as: "emotional_moments",
            type: "string",
          },
          {
            field: "talk_listen_ratio",
            as: "talk_listen_ratio",
            type: "string",
          },
          { field: "engagement_score", as: "call_engagement", type: "string" },
          { field: "rapport_quality", as: "rapport_quality", type: "string" },
          {
            field: "closing_signal_strength",
            as: "closing_signals",
            type: "string",
          },
          { field: "prospect_name", as: "prospect_name", type: "string" },
          { field: "prospect_email", as: "prospect_email", type: "string" },
          { field: "deal_id", as: "deal_id", type: "string" },
          { field: "rep_name", as: "rep_name", type: "string" },
        ],
      ),

      // 4. AI key moment extraction
      aiNode(
        "4",
        "AI extract key moments and coaching opportunities",
        760,
        "You are a sales coaching specialist. From the call transcript and sentiment analysis, identify: the strongest objection raised (and how it was handled), the best rep moment (most effective technique used), the biggest missed opportunity, 3 specific coaching points, a summary of next steps discussed, and any competitive mentions. Return JSON: {strongest_objection, objection_handling, best_rep_moment, missed_opportunity, coaching_point_1, coaching_point_2, coaching_point_3, next_steps_discussed, competitive_mentions, call_duration_estimate}",
        "Transcript: {full_transcript} | Sentiment: {overall_sentiment} | Prospect Trajectory: {prospect_trajectory} | Rep Style: {rep_style} | Emotional Moments: {emotional_moments} | Engagement: {call_engagement}",
        [
          {
            field: "strongest_objection",
            as: "strongest_objection",
            type: "string",
          },
          {
            field: "objection_handling",
            as: "objection_handling",
            type: "string",
          },
          { field: "best_rep_moment", as: "best_rep_moment", type: "string" },
          {
            field: "missed_opportunity",
            as: "missed_opportunity",
            type: "string",
          },
          { field: "coaching_point_1", as: "coaching_1", type: "string" },
          { field: "coaching_point_2", as: "coaching_2", type: "string" },
          { field: "coaching_point_3", as: "coaching_3", type: "string" },
          { field: "next_steps_discussed", as: "next_steps", type: "string" },
          {
            field: "competitive_mentions",
            as: "competitive_mentions",
            type: "string",
          },
          {
            field: "call_duration_estimate",
            as: "call_duration",
            type: "string",
          },
        ],
      ),

      // 5. Deal risk assessment runCode
      n(
        "5",
        "runCode",
        "Assess deal risk based on sentiment and key moments",
        1000,
        {
          runCodeDetails: {
            code: `let riskScore = 50;
const sentiment = variables.overall_sentiment || '';
const trajectory = variables.prospect_trajectory || '';
const closing = variables.closing_signals || '';
if (sentiment === 'positive') riskScore -= 20;
else if (sentiment === 'negative') riskScore += 25;
else if (sentiment === 'mixed') riskScore += 10;
if (trajectory === 'improving') riskScore -= 15;
else if (trajectory === 'declining') riskScore += 20;
if (closing === 'strong') riskScore -= 15;
else if (closing === 'weak') riskScore += 15;
if (variables.competitive_mentions && variables.competitive_mentions !== 'none') riskScore += 10;
riskScore = Math.max(0, Math.min(100, riskScore));
output.deal_risk_score = riskScore;
output.risk_level = riskScore >= 70 ? 'high' : riskScore >= 40 ? 'medium' : 'low';
output.risk_factors = [];
if (sentiment === 'negative') output.risk_factors.push('Negative overall sentiment');
if (trajectory === 'declining') output.risk_factors.push('Declining prospect engagement');
if (variables.competitive_mentions !== 'none') output.risk_factors.push('Competitor mentioned: ' + variables.competitive_mentions);
output.risk_summary = output.risk_factors.length > 0 ? output.risk_factors.join('; ') : 'No significant risk factors identified';
output.needs_coaching = riskScore >= 50 || variables.missed_opportunity !== 'none' ? 'yes' : 'no';`,
            outputVariables: [
              { name: "deal_risk_score", type: "number" },
              { name: "risk_level", type: "string" },
              { name: "risk_summary", type: "string" },
              { name: "needs_coaching", type: "string" },
            ],
          },
        },
      ),

      // 6. Sentiment report generation runCode
      n(
        "6",
        "runCode",
        "Generate comprehensive sentiment analysis report",
        1240,
        {
          runCodeDetails: {
            code: `output.report = '=== SALES CALL SENTIMENT REPORT ===\\n' +
'Call: ' + variables.gong_call_id + ' | Duration: ' + variables.call_duration + '\\n' +
'Rep: ' + variables.rep_name + ' | Prospect: ' + variables.prospect_name + '\\n\\n' +
'SENTIMENT SUMMARY:\\n' +
'- Overall: ' + variables.overall_sentiment + '\\n' +
'- Prospect Trajectory: ' + variables.prospect_trajectory + '\\n' +
'- Rep Style: ' + variables.rep_style + '\\n' +
'- Talk/Listen: ' + variables.talk_listen_ratio + '\\n' +
'- Rapport: ' + variables.rapport_quality + '\\n' +
'- Engagement: ' + variables.call_engagement + '\\n' +
'- Closing Signals: ' + variables.closing_signals + '\\n\\n' +
'DEAL RISK: ' + variables.risk_level + ' (' + variables.deal_risk_score + '/100)\\n' +
'Risk Factors: ' + variables.risk_summary + '\\n\\n' +
'KEY MOMENTS:\\n' +
'- Objection: ' + variables.strongest_objection + '\\n' +
'- Handling: ' + variables.objection_handling + '\\n' +
'- Best Moment: ' + variables.best_rep_moment + '\\n' +
'- Missed Opportunity: ' + variables.missed_opportunity + '\\n\\n' +
'NEXT STEPS: ' + variables.next_steps;
output.report_generated = 'yes';`,
            outputVariables: [
              { name: "report", type: "string" },
              { name: "report_generated", type: "string" },
            ],
          },
        },
      ),

      // 7. CRM deal update HubSpot
      hubspotPostNode(
        "7",
        "Update CRM deal with sentiment analysis and risk",
        1480,
        "crm/v3/objects/deals/{deal_id}",
        [
          {
            key: "properties",
            value:
              '{"notes_last_updated":"Call Sentiment: {overall_sentiment} | Trajectory: {prospect_trajectory} | Risk: {risk_level} ({deal_risk_score}/100) | Engagement: {call_engagement} | Next Steps: {next_steps} | Coaching Needed: {needs_coaching} | Objection: {strongest_objection} | Competition: {competitive_mentions}"}',
          },
        ],
        [
          { name: "id", customName: "updated_deal_id", type: "string" },
          {
            name: "properties.notes_last_updated",
            customName: "sentiment_logged",
            type: "string",
          },
        ],
      ),

      // 8. Sales manager coaching alert Slack
      slackNode(
        "8",
        "Alert sales manager with coaching insights",
        1720,
        "🎯 *Sales Call Sentiment Report*\\n*Rep:* {rep_name} | *Prospect:* {prospect_name}\\n*Call:* {gong_call_id} | *Duration:* {call_duration}\\n\\n*Sentiment:* {overall_sentiment} | *Trajectory:* {prospect_trajectory}\\n*Risk:* {risk_level} ({deal_risk_score}/100) | *Engagement:* {call_engagement}\\n*Rapport:* {rapport_quality} | *Closing Signals:* {closing_signals}\\n\\n*Objection:* {strongest_objection}\\n*Handling:* {objection_handling}\\n*Best Moment:* {best_rep_moment}\\n*Missed Opportunity:* {missed_opportunity}\\n\\n*Coaching Points:*\\n1. {coaching_1}\\n2. {coaching_2}\\n3. {coaching_3}\\n\\n*Next Steps:* {next_steps}\\n*Competition:* {competitive_mentions}\\n*Needs Coaching:* {needs_coaching}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1666. SALES FORECAST GENERATOR (8 nodes)
  // ===================================================================

  "Sales Forecast Generator": {
    summary:
      "Runs on a weekly Monday schedule, extracts pipeline data from HubSpot, analyzes historical win rates with runCode, uses AI to build a forecast model, breaks down rep-level forecasts with runCode, writes the forecast report to Google Sheets, sends an executive summary email, and tracks forecast accuracy in Sheets.",
    nodes: [
      trigger("Schedule: Weekly sales forecast generation", "schedule", {
        schedule: "Every Monday at 7:00 AM",
        howItStarts:
          "Runs every Monday morning to automatically generate the weekly sales forecast by pulling pipeline data, applying historical win rates, and producing rep-level and team-level projections",
        setup:
          "Configure weekly Monday schedule trigger. Ensure HubSpot API token has deal read access across all pipelines. Set up Google Sheets for forecast reports and accuracy tracking.",
        inputFields: [],
      }),

      // 2. Pipeline data extraction HubSpot
      hubspotGetNode(
        "2",
        "Extract current pipeline data from HubSpot",
        280,
        "crm/v3/objects/deals?limit=100&properties=dealname,amount,dealstage,pipeline,closedate,hubspot_owner_id,hs_deal_stage_probability,createdate",
        [
          { name: "total", customName: "total_deals", type: "number" },
          { name: "results", customName: "pipeline_deals", type: "string" },
        ],
      ),

      // 3. Historical win rate analysis runCode
      n(
        "3",
        "runCode",
        "Calculate historical win rates and stage probabilities",
        520,
        {
          runCodeDetails: {
            code: `const deals = JSON.parse(variables.pipeline_deals || '[]');
const totalDeals = parseInt(variables.total_deals || '0');
let totalValue = 0;
let weightedValue = 0;
const stageBreakdown = {};
const ownerBreakdown = {};
deals.forEach(d => {
  const props = d.properties || {};
  const amount = parseFloat(props.amount || '0');
  const prob = parseFloat(props.hs_deal_stage_probability || '0') / 100;
  const stage = props.dealstage || 'unknown';
  const owner = props.hubspot_owner_id || 'unassigned';
  totalValue += amount;
  weightedValue += amount * prob;
  if (!stageBreakdown[stage]) stageBreakdown[stage] = { count: 0, value: 0, weighted: 0 };
  stageBreakdown[stage].count++;
  stageBreakdown[stage].value += amount;
  stageBreakdown[stage].weighted += amount * prob;
  if (!ownerBreakdown[owner]) ownerBreakdown[owner] = { count: 0, value: 0, weighted: 0 };
  ownerBreakdown[owner].count++;
  ownerBreakdown[owner].value += amount;
  ownerBreakdown[owner].weighted += amount * prob;
});
output.total_pipeline_value = totalValue.toFixed(2);
output.weighted_forecast = weightedValue.toFixed(2);
output.total_open_deals = totalDeals;
output.average_deal_size = totalDeals > 0 ? (totalValue / totalDeals).toFixed(2) : '0';
output.stage_breakdown = JSON.stringify(stageBreakdown);
output.owner_breakdown = JSON.stringify(ownerBreakdown);
output.forecast_confidence = weightedValue / totalValue > 0.5 ? 'high' : weightedValue / totalValue > 0.3 ? 'medium' : 'low';`,
            outputVariables: [
              { name: "total_pipeline_value", type: "string" },
              { name: "weighted_forecast", type: "string" },
              { name: "total_open_deals", type: "number" },
              { name: "average_deal_size", type: "string" },
              { name: "stage_breakdown", type: "string" },
              { name: "owner_breakdown", type: "string" },
              { name: "forecast_confidence", type: "string" },
            ],
          },
        },
      ),

      // 4. AI forecast model
      aiNode(
        "4",
        "AI build intelligent sales forecast with insights",
        760,
        "You are a sales forecasting expert. Given the pipeline data, stage breakdown, and win rate analysis, build an intelligent forecast. Identify pipeline risks, upside opportunities, deals likely to slip, and seasonal factors. Provide a best-case, expected, and worst-case forecast scenario. Return JSON: {best_case_forecast, expected_forecast, worst_case_forecast, key_risks, upside_opportunities, deals_at_risk, seasonal_factor, forecast_narrative, action_items, forecast_period}",
        "Pipeline: {total_open_deals} deals | Value: ${total_pipeline_value} | Weighted: ${weighted_forecast} | Avg Deal: ${average_deal_size} | Confidence: {forecast_confidence} | Stage Breakdown: {stage_breakdown} | Owner Breakdown: {owner_breakdown}",
        [
          { field: "best_case_forecast", as: "best_case", type: "string" },
          {
            field: "expected_forecast",
            as: "expected_forecast",
            type: "string",
          },
          { field: "worst_case_forecast", as: "worst_case", type: "string" },
          { field: "key_risks", as: "key_risks", type: "string" },
          { field: "upside_opportunities", as: "upside_opps", type: "string" },
          { field: "deals_at_risk", as: "deals_at_risk", type: "string" },
          { field: "seasonal_factor", as: "seasonal_factor", type: "string" },
          {
            field: "forecast_narrative",
            as: "forecast_narrative",
            type: "string",
          },
          { field: "action_items", as: "action_items", type: "string" },
          { field: "forecast_period", as: "forecast_period", type: "string" },
        ],
      ),

      // 5. Rep-level forecast breakdown runCode
      n("5", "runCode", "Generate rep-level forecast breakdown", 1000, {
        runCodeDetails: {
          code: `const owners = JSON.parse(variables.owner_breakdown || '{}');
const repForecasts = [];
let topRep = { name: '', value: 0 };
let bottomRep = { name: '', value: Infinity };
Object.entries(owners).forEach(([owner, data]) => {
  const rep = { rep_id: owner, deals: data.count, pipeline: data.value.toFixed(2), forecast: data.weighted.toFixed(2), attainment_pct: data.value > 0 ? Math.round((data.weighted / data.value) * 100) : 0 };
  repForecasts.push(owner + ': ' + data.count + ' deals, $' + rep.pipeline + ' pipeline, $' + rep.forecast + ' forecast (' + rep.attainment_pct + '%)');
  if (data.weighted > topRep.value) { topRep = { name: owner, value: data.weighted }; }
  if (data.weighted < bottomRep.value) { bottomRep = { name: owner, value: data.weighted }; }
});
output.rep_forecast_summary = repForecasts.join('\\n');
output.top_performer = topRep.name + ' ($' + topRep.value.toFixed(2) + ')';
output.needs_attention = bottomRep.name + ' ($' + bottomRep.value.toFixed(2) + ')';
output.total_reps = Object.keys(owners).length;
output.forecast_date = new Date().toISOString().split('T')[0];`,
          outputVariables: [
            { name: "rep_forecast_summary", type: "string" },
            { name: "top_performer", type: "string" },
            { name: "needs_attention", type: "string" },
            { name: "total_reps", type: "number" },
            { name: "forecast_date", type: "string" },
          ],
        },
      }),

      // 6. Forecast report builder Sheets
      sheetsNode(
        "6",
        "Write forecast report to Google Sheets",
        1240,
        "WeeklyForecast",
        '[["' +
          new Date().toISOString() +
          '", "{forecast_date}", "{forecast_period}", "{total_open_deals}", "$' +
          '{total_pipeline_value}", "$' +
          '{weighted_forecast}", "$' +
          '{best_case}", "$' +
          '{expected_forecast}", "$' +
          '{worst_case}", "{forecast_confidence}", "{total_reps}", "{top_performer}", "{needs_attention}", "{key_risks}", "{seasonal_factor}"]]',
      ),

      // 7. Executive summary email
      emailNode(
        "7",
        "Send executive forecast summary email",
        1480,
        "sales-leadership@company.com",
        "Weekly Sales Forecast — {forecast_date} | Expected: {expected_forecast}",
        "Sales Leadership Team,\\n\\nHere is your weekly sales forecast for {forecast_period}:\\n\\nForecast Summary:\\n- Total Pipeline: {total_pipeline_value} ({total_open_deals} deals)\\n- Best Case: {best_case}\\n- Expected: {expected_forecast}\\n- Worst Case: {worst_case}\\n- Confidence: {forecast_confidence}\\n- Average Deal Size: {average_deal_size}\\n\\n{forecast_narrative}\\n\\nRep Breakdown:\\n{rep_forecast_summary}\\n\\nTop Performer: {top_performer}\\nNeeds Attention: {needs_attention}\\n\\nKey Risks: {key_risks}\\nUpside Opportunities: {upside_opps}\\nDeals at Risk: {deals_at_risk}\\nSeasonal Factor: {seasonal_factor}\\n\\nAction Items:\\n{action_items}\\n\\nFull report available in the Weekly Forecast spreadsheet.\\n\\nBest regards,\\nSales Operations",
      ),

      // 8. Forecast accuracy tracker Sheets
      sheetsNode(
        "8",
        "Log forecast for accuracy tracking",
        1720,
        "ForecastAccuracy",
        '[["' +
          new Date().toISOString() +
          '", "{forecast_date}", "$' +
          '{expected_forecast}", "$' +
          '{best_case}", "$' +
          '{worst_case}", "$' +
          '{total_pipeline_value}", "{total_open_deals}", "{forecast_confidence}", "", "", ""]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1667. SALES LEADERBOARD DIGEST (7 nodes)
  // ===================================================================

  "Sales Leaderboard Digest": {
    summary:
      "Runs on a daily or weekly schedule, aggregates sales metrics from HubSpot, calculates ranking trends with runCode, uses AI to generate a narrative leaderboard, builds a leaderboard visualization with runCode, distributes the digest via Slack, and sends an email backup distribution.",
    nodes: [
      trigger(
        "Schedule: Daily or weekly sales leaderboard generation",
        "schedule",
        {
          schedule:
            "Every weekday at 5:00 PM (daily) or Friday at 4:00 PM (weekly)",
          howItStarts:
            "Runs on schedule to compile sales performance metrics, generate rankings, and distribute an engaging leaderboard digest to the sales team",
          setup:
            "Configure daily or weekly schedule trigger. Ensure HubSpot API token has deal and owner read access. Configure Slack webhook for the sales channel.",
          inputFields: [],
        },
      ),

      // 2. Sales metrics aggregation HubSpot
      hubspotGetNode(
        "2",
        "Aggregate sales performance metrics from HubSpot",
        280,
        "crm/v3/objects/deals?limit=100&properties=dealname,amount,dealstage,hubspot_owner_id,closedate,createdate,hs_deal_stage_probability&associations=contacts",
        [
          { name: "total", customName: "total_deals", type: "number" },
          { name: "results", customName: "all_deals", type: "string" },
        ],
      ),

      // 3. Ranking trend calculator runCode
      n(
        "3",
        "runCode",
        "Calculate sales rankings and performance trends",
        520,
        {
          runCodeDetails: {
            code: `const deals = JSON.parse(variables.all_deals || '[]');
const repStats = {};
const now = new Date();
const periodStart = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
deals.forEach(d => {
  const props = d.properties || {};
  const owner = props.hubspot_owner_id || 'unknown';
  const amount = parseFloat(props.amount || '0');
  const stage = props.dealstage || '';
  const closeDate = new Date(props.closedate || '');
  if (!repStats[owner]) repStats[owner] = { closed_won: 0, closed_value: 0, open_deals: 0, pipeline_value: 0, activities: 0 };
  if (stage === 'closedwon' && closeDate >= periodStart) {
    repStats[owner].closed_won++;
    repStats[owner].closed_value += amount;
  } else if (stage !== 'closedlost') {
    repStats[owner].open_deals++;
    repStats[owner].pipeline_value += amount;
  }
});
const rankings = Object.entries(repStats)
  .map(([rep, stats]) => ({ rep, ...stats, total_score: stats.closed_value * 2 + stats.pipeline_value * 0.3 }))
  .sort((a, b) => b.total_score - a.total_score);
output.leaderboard_data = JSON.stringify(rankings);
output.top_closer = rankings[0] ? rankings[0].rep + ' ($' + rankings[0].closed_value.toFixed(0) + ' closed)' : 'N/A';
output.top_pipeline = rankings.reduce((max, r) => r.pipeline_value > (max?.pipeline_value || 0) ? r : max, rankings[0]);
output.top_pipeline_rep = output.top_pipeline ? output.top_pipeline.rep + ' ($' + output.top_pipeline.pipeline_value.toFixed(0) + ' pipeline)' : 'N/A';
output.total_closed = rankings.reduce((s, r) => s + r.closed_value, 0).toFixed(2);
output.total_pipeline = rankings.reduce((s, r) => s + r.pipeline_value, 0).toFixed(2);
output.total_reps = rankings.length;
output.period_label = periodStart.toISOString().split('T')[0] + ' to ' + now.toISOString().split('T')[0];`,
            outputVariables: [
              { name: "leaderboard_data", type: "string" },
              { name: "top_closer", type: "string" },
              { name: "top_pipeline_rep", type: "string" },
              { name: "total_closed", type: "string" },
              { name: "total_pipeline", type: "string" },
              { name: "total_reps", type: "number" },
              { name: "period_label", type: "string" },
            ],
          },
        },
      ),

      // 4. AI narrative generator
      aiNode(
        "4",
        "AI generate engaging leaderboard narrative",
        760,
        "You are a sales team motivator and data storyteller. Given the leaderboard rankings and performance data, create an engaging narrative that celebrates top performers, motivates the middle of the pack, and encourages healthy competition. Include witty commentary, performance callouts, and a team rally cry. Return JSON: {headline, narrative_intro, top_performer_callout, rising_star_mention, team_momentum, fun_stat, rally_cry, performance_trend}",
        "Period: {period_label} | Reps: {total_reps} | Closed: ${total_closed} | Pipeline: ${total_pipeline} | Top Closer: {top_closer} | Top Pipeline: {top_pipeline_rep} | Full Rankings: {leaderboard_data}",
        [
          { field: "headline", as: "lb_headline", type: "string" },
          { field: "narrative_intro", as: "lb_intro", type: "string" },
          { field: "top_performer_callout", as: "top_callout", type: "string" },
          { field: "rising_star_mention", as: "rising_star", type: "string" },
          { field: "team_momentum", as: "team_momentum", type: "string" },
          { field: "fun_stat", as: "fun_stat", type: "string" },
          { field: "rally_cry", as: "rally_cry", type: "string" },
          {
            field: "performance_trend",
            as: "performance_trend",
            type: "string",
          },
        ],
      ),

      // 5. Leaderboard visualization runCode
      n("5", "runCode", "Build leaderboard text visualization", 1000, {
        runCodeDetails: {
          code: `const rankings = JSON.parse(variables.leaderboard_data || '[]');
const medals = ['🥇', '🥈', '🥉'];
let board = '';
rankings.forEach((r, i) => {
  const medal = i < 3 ? medals[i] : '  ' + (i + 1) + '.';
  const bar = '█'.repeat(Math.min(20, Math.round(r.closed_value / (parseFloat(variables.total_closed) || 1) * 20)));
  board += medal + ' ' + r.rep + '\\n   Closed: $' + r.closed_value.toFixed(0) + ' (' + r.closed_won + ' deals) | Pipeline: $' + r.pipeline_value.toFixed(0) + ' (' + r.open_deals + ' deals)\\n   ' + bar + '\\n\\n';
});
output.leaderboard_visual = board || 'No data available for this period.';
output.digest_generated = 'yes';
output.generation_timestamp = new Date().toISOString();`,
          outputVariables: [
            { name: "leaderboard_visual", type: "string" },
            { name: "digest_generated", type: "string" },
            { name: "generation_timestamp", type: "string" },
          ],
        },
      }),

      // 6. Digest distribution Slack
      slackNode(
        "6",
        "Distribute leaderboard digest via Slack",
        1240,
        "📊 *{lb_headline}*\\n_{period_label}_\\n\\n{lb_intro}\\n\\n*LEADERBOARD:*\\n{leaderboard_visual}\\n*Team Stats:*\\n- Total Closed: ${total_closed} | Total Pipeline: ${total_pipeline}\\n- Active Reps: {total_reps}\\n\\n{top_callout}\\n{rising_star}\\n\\n*Momentum:* {team_momentum}\\n*Fun Stat:* {fun_stat}\\n*Trend:* {performance_trend}\\n\\n{rally_cry}",
      ),

      // 7. Email backup distribution
      emailNode(
        "7",
        "Send leaderboard digest email to sales team",
        1480,
        "sales-team@company.com",
        "{lb_headline} — {period_label}",
        "Sales Team,\\n\\n{lb_intro}\\n\\nLEADERBOARD:\\n{leaderboard_visual}\\n\\nTeam Performance Summary:\\n- Total Revenue Closed: ${total_closed}\\n- Total Pipeline Value: ${total_pipeline}\\n- Active Reps: {total_reps}\\n\\n{top_callout}\\n\\n{rising_star}\\n\\nTeam Momentum: {team_momentum}\\n\\nFun Stat: {fun_stat}\\n\\nPerformance Trend: {performance_trend}\\n\\n{rally_cry}\\n\\nKeep pushing!\\n\\nSales Operations",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },
};
